/**
 * MaestroReports - Excel import page for Maestro financial data
 * 
 * Allows users to upload Excel files with financial data from Maestro
 * and stores them in the forecasting.maestro_import table.
 * Displays imported data in a scrollable table.
 * Generates Report 9091 PDF (Summary Report - With Committed).
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import UploadMaestroModal from './UploadMaestroModal';
import MaestroDashboardHelp from './MaestroDashboardHelp';
import { formatMaestroBatchOption, maestroBatchCaption } from './maestroBatchLabel';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { List, useListRef } from 'react-window';
import { FilterIcon, FilterTemplateDropdown, useFilterTemplate } from '../common/FilteringTemplate';
import { applyLetterhead, contentStartBelowLetterhead } from '../../utils/pdfLetterhead';
import { exportFilename } from '../../utils/nomenclature';
import useColumnOrder from '../../hooks/useColumnOrder';
import ColumnConfigPanel from '../common/ColumnConfigPanel';
import AuditLogModal from '../common/AuditLogModal';

// All columns from forecasting.maestro_import (excluding system columns)
const TABLE_COLUMNS = [
  // Text / identification
  { key: 'project', label: 'Project', type: 'text' },
  { key: 'prefix', label: 'Prefix', type: 'text' },
  { key: 'phase', label: 'Phase', type: 'text' },
  { key: 'activity', label: 'Activity', type: 'text' },
  { key: 'cost_type', label: 'Cost Type', type: 'text' },
  // Budget
  { key: 'orig_bud', label: 'Orig Bud', type: 'number' },
  { key: 'revised_bud', label: 'Revised Bud', type: 'number' },
  { key: 'bud_modif', label: 'Bud Modif', type: 'number' },
  { key: 'bud_bal', label: 'Bud Bal', type: 'number' },
  { key: 'tot_bud_bal', label: 'Tot Bud Bal', type: 'number' },
  // Actual
  { key: 'actual', label: 'Actual', type: 'number' },
  { key: 'actual_plus_accrued', label: 'Actual+Accrued', type: 'number' },
  { key: 'accrued', label: 'Accrued', type: 'number' },
  // Committed / PO
  { key: 'comm_costs', label: 'Comm Costs', type: 'number' },
  { key: 'po_total', label: 'PO Total', type: 'number' },
  { key: 'po_inv', label: 'PO Inv', type: 'number' },
  { key: 'po_bal', label: 'PO Bal', type: 'number' },
  { key: 'total_and_po', label: 'Total & PO', type: 'number' },
  // Selling / Revenue
  { key: 'revised_selling_bud', label: 'Rev Selling Bud', type: 'number' },
  { key: 'orig_selling_bud', label: 'Orig Selling Bud', type: 'number' },
  { key: 'selling_bud_modif', label: 'Selling Bud Modif', type: 'number' },
  // Production / Billing / Other
  { key: 'production', label: 'Production', type: 'number' },
  { key: 'projection', label: 'Projection', type: 'number' },
  { key: 'billing', label: 'Billing', type: 'number' },
  { key: 'contingency', label: 'Contingency', type: 'number' },
  { key: 'to_complete', label: 'To Complete', type: 'number' },
  // Fulfill quantities
  { key: 'rev_qty_to_fulfill', label: 'Rev Qty Fulfill', type: 'number' },
  { key: 'mod_qty_to_fulfill', label: 'Mod Qty Fulfill', type: 'number' },
  { key: 'orig_qty_to_fulfill', label: 'Orig Qty Fulfill', type: 'number' },
  // Percentage
  { key: 'pct_budget_spent', label: '% Bud Spent', type: 'percent' },
  { key: 'pct_budget_spent_tot', label: '% Bud Spent Tot', type: 'percent' },
  // (Q) variants
  { key: 'orig_bud_q', label: 'Orig Bud(Q)', type: 'number' },
  { key: 'revised_bud_q', label: 'Revised Bud(Q)', type: 'number' },
  { key: 'bud_modif_q', label: 'Bud Modif(Q)', type: 'number' },
  { key: 'bud_bal_q', label: 'Bud Bal(Q)', type: 'number' },
  { key: 'tot_bud_bal_q', label: 'Tot Bud Bal(Q)', type: 'number' },
  { key: 'actual_q', label: 'Actual(Q)', type: 'number' },
  { key: 'actual_plus_accrued_q', label: 'Actual+Accrued(Q)', type: 'number' },
  { key: 'accrued_q', label: 'Accrued(Q)', type: 'number' },
  { key: 'po_total_q', label: 'PO Total(Q)', type: 'number' },
  { key: 'po_inv_q', label: 'PO Inv(Q)', type: 'number' },
  { key: 'po_bal_q', label: 'PO Bal(Q)', type: 'number' },
  { key: 'total_and_po_q', label: 'Total & PO(Q)', type: 'number' },
  { key: 'revised_selling_bud_q', label: 'Rev Selling Bud(Q)', type: 'number' },
  { key: 'orig_selling_bud_q', label: 'Orig Selling Bud(Q)', type: 'number' },
  { key: 'selling_bud_modif_q', label: 'Selling Bud Modif(Q)', type: 'number' },
  { key: 'production_q', label: 'Production(Q)', type: 'number' },
  { key: 'projection_q', label: 'Projection(Q)', type: 'number' },
  { key: 'billing_q', label: 'Billing(Q)', type: 'number' },
  { key: 'contingency_q', label: 'Contingency(Q)', type: 'number' },
  { key: 'to_complete_q', label: 'To Complete(Q)', type: 'number' },
  { key: 'pct_budget_spent_q', label: '% Bud Spent(Q)', type: 'percent' },
  { key: 'pct_budget_spent_tot_q', label: '% Bud Spent Tot(Q)', type: 'percent' },
];

const TABLE_MAX_HEIGHT = 486;

// ── Per-user column personalization (persisted in localStorage) ──────────────
const COLUMN_ORDER_STORAGE_KEY   = 'maestro-reports-column-order';
const VISIBLE_COLS_STORAGE_KEY   = 'maestro-reports-visible-columns';
// Minimal column set used by the "Min" button in the column chooser
const MIN_COLUMN_KEYS = ['project', 'prefix', 'phase', 'activity', 'cost_type'];

const allColumnKeys = () => TABLE_COLUMNS.map(c => c.key);

// Dollar-denominated columns for the CAD/USD view toggle. (Q) columns are
// quantities/hours and "Qty to Fulfill" columns are unit counts — never converted.
// Percent columns are ratios — also never converted.
const DOLLAR_COLUMN_KEYS = (() => {
  const keys = new Set();
  TABLE_COLUMNS.forEach(c => {
    if (c.type !== 'number') return;
    if (c.key.endsWith('_q')) return;
    if (c.key.includes('qty_to_fulfill')) return;
    keys.add(c.key);
  });
  return keys;
})();

const loadVisibleColumns = () => {
  try {
    const raw = localStorage.getItem(VISIBLE_COLS_STORAGE_KEY);
    if (!raw) return new Set(allColumnKeys());
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr) || arr.length === 0) return new Set(allColumnKeys());
    const validKeys = new Set(allColumnKeys());
    const filtered = arr.filter(k => validKeys.has(k));
    return filtered.length > 0 ? new Set(filtered) : new Set(allColumnKeys());
  } catch {
    return new Set(allColumnKeys());
  }
};

// ── Default sort (Activity) + per-user persistence, like the Columns chooser ──
const CURRENCY_VIEW_STORAGE_PREFIX = 'maestro-reports-currency-view:';
const loadStoredCurrencyView = (projectId) => {
  if (!projectId) return null;
  try {
    const saved = localStorage.getItem(`${CURRENCY_VIEW_STORAGE_PREFIX}${projectId}`);
    if (saved === 'native' || /^[A-Z]{3}$/.test(saved || '')) return saved;
  } catch { /* storage blocked */ }
  return null;
};

const SORT_STORAGE_KEY = 'maestro-reports-sort';
const DEFAULT_SORT = { key: 'activity', direction: 'asc' };
const loadStoredSort = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(SORT_STORAGE_KEY));
    if (saved && saved.key && (saved.direction === 'asc' || saved.direction === 'desc')) return saved;
  } catch { /* corrupted value — fall through to default */ }
  return DEFAULT_SORT;
};

const saveVisibleColumns = (visibleSet) => {
  try {
    localStorage.setItem(VISIBLE_COLS_STORAGE_KEY, JSON.stringify([...visibleSet]));
  } catch { /* storage full / private mode — personalization just won't persist */ }
};

const formatValue = (value, type) => {
  if (value === null || value === undefined || value === '') return '-';
  if (type === 'number') {
    const num = parseFloat(value);
    if (isNaN(num)) return '-';
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  if (type === 'percent') {
    const num = parseFloat(value);
    if (isNaN(num)) return '-';
    return num.toFixed(2) + '%';
  }
  // Trim whitespace — raw Excel values often have trailing spaces that cause
  // the browser to see overflow and show an ellipsis on otherwise-fitting text.
  return String(value).trim();
};

// ─── Report 9091 PDF Generator ──────────────────────────────────────────────

/**
 * Dollar-valued keys in the report that should be converted when the company's
 * currency differs from the master project's currency.
 * Hours/quantity keys (hoursEstd, hoursActual, hoursVariance) are excluded.
 */
const FINANCIAL_KEYS = new Set([
  'revenueEstd', 'revenueActual', 'revenueVariance',
  'costEstd', 'costActual', 'remainingPO', 'totalCostCommitted', 'costVariance',
]);

/**
 * Report column definitions for Report 9091.
 * Each entry maps to a column in the PDF table.
 */
// Landscape letter = 279.4mm wide. With 7mm margins each side = 265.4mm usable.
// Column widths are tuned to fit within that space.
// Total fixed cols (12 numeric + Activity): ~225mm, Description gets the remainder (~40mm).
const REPORT_COLUMNS = [
  { header: 'Activity', dataKey: 'activityCode', width: 14 },
  { header: 'Description', dataKey: 'description', width: 'auto' },
  { header: "Hours\nEst'd", dataKey: 'hoursEstd', width: 17 },
  { header: "Hours\nActual", dataKey: 'hoursActual', width: 17 },
  { header: "Hours\nVariance", dataKey: 'hoursVariance', width: 17 },
  { header: "Revenue\nEst'd", dataKey: 'revenueEstd', width: 19 },
  { header: "Revenue\nActual", dataKey: 'revenueActual', width: 19 },
  { header: "Revenue\nVariance", dataKey: 'revenueVariance', width: 19 },
  { header: "Cost\nEst'd", dataKey: 'costEstd', width: 21 },
  { header: "Cost\nActual", dataKey: 'costActual', width: 21 },
  { header: "Remaining PO\nCommitted", dataKey: 'remainingPO', width: 21 },
  { header: "Total Cost Incl\nCommitted", dataKey: 'totalCostCommitted', width: 22 },
  { header: "Cost\nVariance", dataKey: 'costVariance', width: 22 },
];

/** Numeric columns (everything except Activity and Description) */
const NUMERIC_KEYS = REPORT_COLUMNS.slice(2).map(c => c.dataKey);

/** Format a number for display in the PDF (2 decimal places, comma-separated) */
const fmtNum = (v) => {
  if (v === null || v === undefined) return '0.00';
  const n = parseFloat(v);
  if (isNaN(n)) return '0.00';
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};


/**
 * Render one company's disciplines into a jsPDF doc.
 * Each discipline starts on a new page (first discipline uses the current page).
 *
 * @param {object} doc - jsPDF instance
 * @param {object} project - project metadata (jobNumber, jobName, masterCountry)
 * @param {object} company - company data (prefix, divisionName, countryCode, disciplines)
 * @param {Date}   now - report generation timestamp
 * @param {object} exchangeRates - { 'CAD_USD': { rate, updatedAt }, 'USD_CAD': { rate, updatedAt } }
 */
const renderCompanyToPdf = (doc, project, company, now, exchangeRates = {}, options = {}) => {
  const reportTitle = options.reportTitle || 'Report 9091 - Summary Report - With Committed';
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const dateStr = now.toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });
  const timeStr = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  const marginLeft = 7;
  const marginRight = 7;

  // ── Currency conversion setup ──
  const masterCountry = project.masterCountry || null;
  const companyCountry = company.countryCode || null;
  const needsConversion = !!(masterCountry && companyCountry && masterCountry !== companyCountry);
  let conversionRate = 1;
  let conversionLabel = null;
  if (needsConversion) {
    const rateKey = `${companyCountry}_${masterCountry}`;
    const rateData = exchangeRates[rateKey];
    if (rateData) {
      conversionRate = rateData.rate;
      const rateDate = rateData.updatedAt
        ? new Date(rateData.updatedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
        : 'unknown date';
      conversionLabel = `* Dollar values converted from ${companyCountry} to ${masterCountry} at 1 ${companyCountry} = ${conversionRate.toFixed(6)} ${masterCountry} (rate set ${rateDate})`;
    }
  }
  /** Apply conversion to a value only if it's a financial (dollar) key */
  const conv = (key, v) => {
    const n = parseFloat(v) || 0;
    return (needsConversion && FINANCIAL_KEYS.has(key)) ? n * conversionRate : n;
  };

  let isFirstDiscipline = true;

  for (const disc of company.disciplines) {
    if (!isFirstDiscipline) doc.addPage();

    const headerStartPage = doc.internal.getNumberOfPages();
    // Letterhead only exists on the first page of the doc — later disciplines
    // start fresh and must not inherit the first-page letterheadY gap.
    let y = isFirstDiscipline ? (options.letterheadY || 10) : 10;
    isFirstDiscipline = false;

    // ── Header ──
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(company.divisionName || company.prefix || '', pageWidth / 2, y, { align: 'center' });
    y += 5;

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(reportTitle, pageWidth / 2, y, { align: 'center' });
    y += 5;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`${disc.projectCode}  ${project.jobName || ''}`, pageWidth / 2, y, { align: 'center' });
    y += 4;

    doc.text('For transactions from beginning to end', pageWidth / 2, y, { align: 'center' });
    y += 6;

    // ── Build table body ──
    const body = [];
    const grandTotals = {};
    for (const key of NUMERIC_KEYS) grandTotals[key] = 0;

    for (const phase of disc.phases) {
      body.push([
        { content: phase.phaseCode, styles: { fontStyle: 'bold', fillColor: false } },
        { content: phase.phaseDescription, styles: { fontStyle: 'bold', fillColor: false } },
        ...NUMERIC_KEYS.map(() => ({ content: '', styles: { fillColor: false } })),
      ]);

      const visibleActivities = phase.activities
        .sort((a, b) => a.activityCode.localeCompare(b.activityCode, undefined, { numeric: true }));

      for (const act of visibleActivities) {
        body.push([
          act.activityCode,
          act.description,
          ...NUMERIC_KEYS.map(k => fmtNum(conv(k, act[k]))),
        ]);
      }

      // Phase totals — sum already-converted values
      const phaseTotals = {};
      for (const key of NUMERIC_KEYS) {
        phaseTotals[key] = visibleActivities.reduce((s, r) => s + conv(key, r[key]), 0);
      }
      body.push([
        { content: '', styles: { fontStyle: 'bold' } },
        { content: `TOTAL  ${phase.phaseDescription}`, styles: { fontStyle: 'bold' } },
        ...NUMERIC_KEYS.map(k => ({ content: fmtNum(phaseTotals[k]), styles: { fontStyle: 'bold' } })),
      ]);
      body.push(REPORT_COLUMNS.map(() => ({ content: '', styles: { fillColor: false, minCellHeight: 2 } })));

      for (const key of NUMERIC_KEYS) grandTotals[key] += phaseTotals[key];
    }

    // ── TOTAL PROJECT ──
    body.push([
      { content: '', styles: { fontStyle: 'bold' } },
      { content: 'TOTAL PROJECT', styles: { fontStyle: 'bold' } },
      ...NUMERIC_KEYS.map(k => ({ content: fmtNum(grandTotals[k]), styles: { fontStyle: 'bold' } })),
    ]);

    // ── TOTAL PROFIT ──
    // Col 5 = Est'd Profit (revenueEstd - costEstd)
    // Col 6 = Actual Profit (revenueActual - costActual)
    // Matches Maestro's two-value layout on the TOTAL PROFIT row.
    const estdProfit   = (grandTotals.revenueEstd   || 0) - (grandTotals.costEstd   || 0);
    const actualProfit = (grandTotals.revenueActual || 0) - (grandTotals.costActual || 0);
    body.push([
      { content: '', styles: { fontStyle: 'bold' } },
      { content: 'TOTAL PROFIT (Est/Actual)', styles: { fontStyle: 'bold' } },
      '', '', '',
      { content: fmtNum(estdProfit),   styles: { fontStyle: 'bold' } },
      { content: fmtNum(actualProfit), styles: { fontStyle: 'bold' } },
      '', '', '', '', '', '',
    ]);

    autoTable(doc, {
      startY: y,
      head: [REPORT_COLUMNS.map(c => c.header)],
      body,
      theme: 'plain',
      styles: {
        fontSize: 6,
        cellPadding: { top: 0.8, right: 1.5, bottom: 0.8, left: 1.5 },
        overflow: 'linebreak',
        lineColor: [0, 0, 0],
        lineWidth: 0,
        textColor: [0, 0, 0],
        valign: 'middle',
      },
      headStyles: {
        fontStyle: 'bold',
        fontSize: 6,
        textColor: [0, 0, 80],
        fillColor: false,
        halign: 'center',
        valign: 'bottom',
        lineWidth: { bottom: 0.3 },
        lineColor: [0, 0, 80],
        cellPadding: { top: 1, right: 1.5, bottom: 1, left: 1.5 },
      },
      columnStyles: (() => {
        const styles = {};
        REPORT_COLUMNS.forEach((col, i) => {
          styles[i] = {
            halign: i <= 1 ? 'left' : 'right',
            cellWidth: col.width === 'auto' ? undefined : col.width,
          };
        });
        return styles;
      })(),
      margin: { left: marginLeft, right: marginRight, top: 10, bottom: 14 },
      tableWidth: pageWidth - marginLeft - marginRight,
      didDrawPage: () => {
        const pg = doc.internal.getNumberOfPages();
        if (pg > headerStartPage) {
          doc.setFontSize(8);
          doc.setFont('helvetica', 'normal');
          doc.text(
            `${reportTitle} - ${disc.projectCode}  ${project.jobName || ''} (continued)`,
            pageWidth / 2, 7, { align: 'center' }
          );
        }
      },
    });
  }

  // ── Page footers ──
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.2);
    // Reserve extra vertical space for conversion footnote when present
    const footerLineY = conversionLabel ? pageHeight - 16 : pageHeight - 12;
    doc.line(marginLeft, footerLineY, pageWidth - marginRight, footerLineY);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(120, 120, 120);
    doc.setFont('helvetica', 'normal');
    doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, footerLineY + 4, { align: 'center' });
    doc.text(`${dateStr}, ${timeStr}`, pageWidth - marginRight, footerLineY + 4, { align: 'right' });
    if (conversionLabel) {
      doc.setFontSize(6.5);
      doc.setFont('helvetica', 'italic');
      doc.text(conversionLabel, marginLeft, footerLineY + 8);
    }
    doc.setTextColor(0, 0, 0);
  }
};

/**
 * Generate Report 9091 PDFs from structured report data.
 * Produces one PDF per company (prefix), each with disciplines as sections.
 * Works identically with single-company data (produces one PDF).
 * Dollar values for companies whose currency differs from the master project's
 * currency are automatically converted using the provided exchange rates.
 *
 * @param {Object} reportData - Structured data from /report-data endpoint
 *   (includes project.masterCountry, companies[].countryCode, exchangeRates)
 */
const generate9091Report = async (reportData, branding = {}) => {
  const { project, companies, exchangeRates = {} } = reportData;
  if (!companies || companies.length === 0) return;

  const now = new Date();

  for (const company of companies) {
    if (!company.disciplines || company.disciplines.length === 0) continue;

    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });
    const { y: letterheadY } = await applyLetterhead(doc, {
      ...branding,
      jobNumber: project.jobNumber,
      jobName: project.jobName,
      margin: 10,
    });
    renderCompanyToPdf(doc, project, company, now, exchangeRates, { letterheadY });

    doc.save(exportFilename({
      job: project.jobNumber,
      reportSlug: `Report_9091_${company.prefix || 'company'}`,
      ext: 'pdf',
    }));
  }
};

/**
 * Consolidate report data from multiple companies into a single unified dataset.
 *
 * All currency conversions are applied before summing so the resulting values
 * are expressed in the master project's currency. Activities with the same
 * code + description in the same phase/discipline are summed across companies.
 *
 * The output is shaped identically to the original reportData so it can be
 * passed directly to generate9091Report — which will produce a single PDF
 * because there is now only one synthetic "company".
 *
 * @param {Object} reportData - Structured data from /report-data endpoint
 * @returns {Object} - reportData shaped with a single consolidated company
 */
const consolidateReportData = (reportData) => {
  const { project, companies, exchangeRates = {} } = reportData;
  const masterCountry = project.masterCountry || null;

  // Build a per-company converter that applies currency conversion before summing
  const buildConv = (company) => {
    const needsConv = !!(
      masterCountry && company.countryCode && masterCountry !== company.countryCode
    );
    if (!needsConv) return (key, v) => parseFloat(v) || 0;
    const rateKey = `${company.countryCode}_${masterCountry}`;
    const rate = exchangeRates[rateKey]?.rate || 1;
    return (key, v) => {
      const n = parseFloat(v) || 0;
      return FINANCIAL_KEYS.has(key) ? n * rate : n;
    };
  };

  // disciplineCode → { meta, byPhase: { phaseRaw → { meta, byActivity: { key → summed } } } }
  const byDisc = {};

  for (const company of companies) {
    const conv = buildConv(company);
    for (const disc of company.disciplines) {
      if (!byDisc[disc.disciplineCode]) {
        byDisc[disc.disciplineCode] = {
          projectCode:    disc.projectCode,
          disciplineCode: disc.disciplineCode,
          disciplineName: disc.disciplineName,
          byPhase: {},
        };
      }
      const discEntry = byDisc[disc.disciplineCode];

      for (const phase of disc.phases) {
        if (!discEntry.byPhase[phase.phaseRaw]) {
          discEntry.byPhase[phase.phaseRaw] = {
            phaseCode:        phase.phaseCode,
            phaseDescription: phase.phaseDescription,
            phaseRaw:         phase.phaseRaw,
            byActivity: {},
          };
        }
        const phaseEntry = discEntry.byPhase[phase.phaseRaw];

        for (const act of phase.activities) {
          if (EXCLUDED_ACTIVITY_CODES.has(act.activityCode)) continue;
          // Key on both code and description so different activities don't merge
          const actKey = `${act.activityCode}||${act.description}`;
          if (!phaseEntry.byActivity[actKey]) {
            const blank = { activityCode: act.activityCode, description: act.description };
            for (const key of NUMERIC_KEYS) blank[key] = 0;
            phaseEntry.byActivity[actKey] = blank;
          }
          const actEntry = phaseEntry.byActivity[actKey];
          for (const key of NUMERIC_KEYS) {
            actEntry[key] += conv(key, act[key]);
          }
        }
      }
    }
  }

  // Serialize into the structure renderCompanyToPdf expects
  const disciplines = Object.values(byDisc)
    .map(disc => ({
      projectCode:    disc.projectCode,
      disciplineCode: disc.disciplineCode,
      disciplineName: disc.disciplineName,
      phases: Object.values(disc.byPhase)
        .map(phase => ({
          phaseCode:        phase.phaseCode,
          phaseDescription: phase.phaseDescription,
          phaseRaw:         phase.phaseRaw,
          activities: Object.values(phase.byActivity)
            .sort((a, b) => a.activityCode.localeCompare(b.activityCode, undefined, { numeric: true })),
        }))
        .sort((a, b) => a.phaseCode.localeCompare(b.phaseCode, undefined, { numeric: true })),
    }))
    .sort((a, b) => a.disciplineCode.localeCompare(b.disciplineCode, undefined, { numeric: true }));

  return {
    project,
    // Single synthetic company = the master division
    companies: [{
      prefix:      project.divisionNumber || 'CONSOLIDATED',
      divisionName: project.divisionName  || 'Consolidated',
      countryCode:  masterCountry,
      disciplines,
    }],
    // Conversion already baked in — renderCompanyToPdf must not convert again
    exchangeRates: {},
  };
};

// ─── Consolidated Summary PDF (18-column Excel-matching layout) ─────────────

/**
 * Cost-valued keys for the summary report that require FX conversion.
 * Hours keys are intentionally excluded — never converted.
 */
const SUMMARY_FINANCIAL_KEYS = new Set([
  'costEstd', 'actualPlusAccrued', 'totalCostCommitted',
]);

// Phases are considered labor (hours) only when their description explicitly names
// a labor/staff category. All other phases — Contract Expenses, Room & Board,
// Revenues, and all Extras phases (81xxx-87xxx) — store unit quantities or dollar
// amounts in Actual(Q), NOT hours, and must be excluded from hours columns.
// When the import file includes the Maestro "Group" column, group_col is set to
// values like 'L-Labour', 'M-Material', 'R-Revenue'. The exact 'L-Labour' check
// replaces the former keyword-regex and correctly identifies labor activities
// including those in extras phases (81xxx-87xxx) that happen to be labour rows.
// For legacy imports without group_col (null), fall back to the phase-description
// keyword filter so existing data continues to display hours correctly.
const IS_LABOR_GROUP = 'L-Labour';
const LABOR_PHASE_RE = /labour|labor|staff/i;

// Activities excluded from the consolidated report entirely (no rows, no cost/hour totals).
const EXCLUDED_ACTIVITY_CODES = new Set(['00105']);

/**
 * Aggregate reportData into a per-discipline → per-phase → per-activity
 * structure that carries both per-prefix sub-totals and cross-prefix totals.
 *
 * Returns:
 *   {
 *     prefixes: string[],            // sorted unique prefix codes
 *     prefixMeta: { [pfx]: { divisionName, countryCode } },
 *     disciplines: [
 *       { disciplineCode, disciplineName,
 *         phases: [
 *           { phaseCode, phaseDescription, phaseRaw,
 *             activities: [
 *               { activityCode, description,
 *                 perPrefix: { [pfx]: { hoursActual, costActual } },
 *                 hoursEstd, hoursActual, colK, billing,
 *                 costEstd, costActualTotal, totalAndPO }
 *             ] }
 *         ] }
 *     ]
 *   }
 */
const buildConsolidatedSummaryData = (reportData) => {
  const { project, companies, exchangeRates = {} } = reportData;
  const masterCountry = project.masterCountry || null;

  // Per-company FX converter (same pattern as consolidateReportData)
  const buildConv = (company) => {
    const needsConv = !!(
      masterCountry && company.countryCode && masterCountry !== company.countryCode
    );
    if (!needsConv) return (key, v) => parseFloat(v) || 0;
    const rateKey = `${company.countryCode}_${masterCountry}`;
    const rate = exchangeRates[rateKey]?.rate || 1;
    return (key, v) => {
      const n = parseFloat(v) || 0;
      return SUMMARY_FINANCIAL_KEYS.has(key) ? n * rate : n;
    };
  };

  // Collect sorted unique prefixes and their metadata
  const prefixSet = new Set();
  const prefixMeta = {};
  for (const company of companies) {
    prefixSet.add(company.prefix);
    prefixMeta[company.prefix] = {
      divisionName: company.divisionName,
      countryCode: company.countryCode,
    };
  }
  const prefixes = Array.from(prefixSet).sort();

  // disciplineCode → { meta, byPhase: { phaseRaw → { meta, byActivity: { key → entry } } } }
  const byDisc = {};

  for (const company of companies) {
    const conv = buildConv(company);
    const pfx = company.prefix;

    for (const disc of company.disciplines) {
      if (!byDisc[disc.disciplineCode]) {
        byDisc[disc.disciplineCode] = {
          disciplineCode: disc.disciplineCode,
          disciplineName: disc.disciplineName,
          byPhase: {},
        };
      }
      const discEntry = byDisc[disc.disciplineCode];

      for (const phase of disc.phases) {
        if (!discEntry.byPhase[phase.phaseRaw]) {
          discEntry.byPhase[phase.phaseRaw] = {
            phaseCode: phase.phaseCode,
            phaseDescription: phase.phaseDescription,
            phaseRaw: phase.phaseRaw,
            byActivity: {},
          };
        }
        const phaseEntry = discEntry.byPhase[phase.phaseRaw];

        for (const act of phase.activities) {
          if (EXCLUDED_ACTIVITY_CODES.has(act.activityCode)) continue;

          // Actual(Q) / Orig Bud(Q) only represents labor hours for L-Labour activities.
          // Non-labor groups (M-Material, S-Sub-contractors, R-Revenue, etc.) store unit
          // counts or dollar amounts in (Q).
          // - New imports with group_col populated: use exact 'L-Labour' group match.
          // - Legacy imports without group_col (null): fall back to phase-description
          //   keyword filter so existing data displays hours correctly.
          const isLaborActivity = act.groupCol != null
            ? act.groupCol === IS_LABOR_GROUP
            : LABOR_PHASE_RE.test(phase.phaseDescription);

          const actKey = `${act.activityCode}||${act.description}`;
          if (!phaseEntry.byActivity[actKey]) {
            const perPrefix = {};
            for (const p of prefixes) perPrefix[p] = { hoursActual: 0, costActual: 0 };
            phaseEntry.byActivity[actKey] = {
              activityCode: act.activityCode,
              description: act.description,
              perPrefix,
              hoursEstd: 0,
              hoursActual: 0,
              colK: 0,
              billing: 0,
              costEstd: 0,
              costActualTotal: 0,
              totalAndPO: 0,
            };
          }
          const entry = phaseEntry.byActivity[actKey];

          // Per-prefix sub-columns: hours only for labor activities, cost always accumulated
          if (isLaborActivity) {
            entry.perPrefix[pfx].hoursActual += parseFloat(act.hoursActual) || 0;
          }
          entry.perPrefix[pfx].costActual += conv('actualPlusAccrued', act.actualPlusAccrued);

          // Cross-prefix totals: hours only for labor activities
          if (isLaborActivity) {
            entry.hoursEstd   += parseFloat(act.hoursEstd)   || 0;
            entry.hoursActual += parseFloat(act.hoursActual) || 0;
          }
          // colK: revenueEstd (revised_selling_bud) — same field for all row types
          entry.colK             += parseFloat(act.revenueEstd) || 0;
          entry.billing          += parseFloat(act.revenueActual) || 0;
          entry.costEstd         += conv('costEstd', act.costEstd);
          entry.costActualTotal  += conv('actualPlusAccrued', act.actualPlusAccrued);
          entry.totalAndPO       += conv('totalCostCommitted', act.totalCostCommitted);
        }
      }
    }
  }

  // Serialize into sorted arrays
  const disciplines = Object.values(byDisc)
    .map(disc => ({
      disciplineCode: disc.disciplineCode,
      disciplineName: disc.disciplineName,
      phases: Object.values(disc.byPhase)
        .map(phase => ({
          phaseCode: phase.phaseCode,
          phaseDescription: phase.phaseDescription,
          phaseRaw: phase.phaseRaw,
          activities: Object.values(phase.byActivity)
            .sort((a, b) => a.activityCode.localeCompare(b.activityCode, undefined, { numeric: true })),
        }))
        .sort((a, b) => a.phaseCode.localeCompare(b.phaseCode, undefined, { numeric: true })),
    }))
    .sort((a, b) => a.disciplineCode.localeCompare(b.disciplineCode, undefined, { numeric: true }));

  // Drop disciplines with no data at all (every displayed value zero). Maestro
  // exports placeholder rows (e.g. a "General" discipline holding an all-zero
  // 90001 Revenue line); since each discipline gets its own page, an empty one
  // wasted the whole first page of the PDF (job 61075).
  const activityHasValue = (act) =>
    Object.values(act.perPrefix).some(pp => pp.hoursActual !== 0 || pp.costActual !== 0) ||
    act.hoursEstd !== 0 || act.hoursActual !== 0 || act.colK !== 0 || act.billing !== 0 ||
    act.costEstd !== 0 || act.costActualTotal !== 0 || act.totalAndPO !== 0;
  const activeDisciplines = disciplines.filter(disc =>
    disc.phases.some(phase => phase.activities.some(activityHasValue))
  );

  // Drop prefixes that have no data at all (all hours and costs are zero across
  // every activity). This prevents empty company columns appearing in the PDF
  // when, for example, a prefix exists in project metadata but has no import rows.
  const activePrefixes = prefixes.filter(pfx =>
    activeDisciplines.some(disc =>
      disc.phases.some(phase =>
        phase.activities.some(act => {
          const pp = act.perPrefix[pfx];
          return pp && (pp.hoursActual !== 0 || pp.costActual !== 0);
        })
      )
    )
  );

  return { prefixes: activePrefixes, prefixMeta, disciplines: activeDisciplines };
};

/**
 * Currency sentence under a consolidated title.
 * Master project currency when the Division is set; otherwise the single
 * company currency when every prefix shares one. Empty when mixed and unknown.
 */
function consolidatedCurrencyLine(project, prefixes, prefixMeta) {
  const master = project?.masterCountry || null;
  if (master) {
    const who = [project.divisionName, project.divisionNumber].filter(Boolean).join(' ');
    return who ? `All dollar values in ${master} (${who})` : `All dollar values in ${master}`;
  }
  const codes = [...new Set((prefixes || []).map((pfx) => prefixMeta?.[pfx]?.countryCode).filter(Boolean))];
  if (codes.length === 1) return `All dollar values in ${codes[0]}`;
  return '';
}

/** Draws the currency sentence under the title. Returns extra mm the table must drop. */
function drawConsolidatedCurrencyLine(doc, line, titleY, pageW) {
  if (!line) return 0;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(55, 65, 81);
  doc.text(line, pageW / 2, titleY + 4.2, { align: 'center' });
  doc.setTextColor(0, 0, 0);
  return 3.5;
}

/** True when a consolidated PDF would add two currencies with no master Division to convert into. */
function consolidatedCurrencyBlocked(reportData) {
  if (reportData?.project?.masterCountry) return false;
  const codes = [...new Set((reportData?.companies || []).map((c) => c.countryCode).filter(Boolean))];
  return codes.length > 1;
}

/**
 * Render the consolidated summary PDF — dynamic per-prefix columns + 11 fixed total columns.
 *
 * @param {object} doc          - jsPDF instance (landscape letter, already created)
 * @param {object} project      - { jobNumber, jobName, divisionName, masterCountry }
 * @param {object} summaryData  - output of buildConsolidatedSummaryData
 * @param {object} exchangeRates - raw exchangeRates map (for footnote)
 * @param {Date}   now          - timestamp for footer
 * @param {object} [options]    - { letterheadY } Y below the letterhead hairline on page 1
 */
const renderConsolidatedSummaryToPdf = (doc, project, summaryData, exchangeRates, now, options = {}) => {
  const { prefixes, prefixMeta, disciplines } = summaryData;
  const masterCountry = project.masterCountry || null;
  const PAGE_W = doc.internal.pageSize.getWidth();
  const PAGE_H = doc.internal.pageSize.getHeight();
  const MARGIN = 7;
  const USABLE = PAGE_W - MARGIN * 2; // 265.4mm
  const FONT_SIZE = 6;

  const dateStr = now.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const timeStr = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

  // FX footnote (first matching prefix with a different currency)
  let fxFootnote = '';
  for (const pfx of prefixes) {
    const meta = prefixMeta[pfx];
    if (masterCountry && meta.countryCode && meta.countryCode !== masterCountry) {
      const rateKey = `${meta.countryCode}_${masterCountry}`;
      const rate = exchangeRates[rateKey]?.rate;
      if (rate) {
        fxFootnote = `* ${meta.countryCode} cost values converted to ${masterCountry} at 1 ${meta.countryCode} = ${rate.toFixed(6)} ${masterCountry}`;
        break;
      }
    }
  }

  // ── Column widths ──────────────────────────────────────────────────────────
  // 11 fixed total columns (H–R). Widths based on Helvetica 5.5pt metrics:
  // worst-case hours "84095.75" (8 chars) ≈ 8.0mm → 9mm HRS col; cost "3,521,815.33"
  // (13 chars) ≈ 10.6mm → needs 12mm+ in COST col.
  const TOTAL_COL_WIDTHS = [10, 10, 10, 15, 14, 14, 14, 14, 9, 14, 13]; // sum = 127mm
  const TOTAL_COL_SUM = TOTAL_COL_WIDTHS.reduce((a, b) => a + b, 0);
  const DESC_W = 42;
  // Remaining width shared equally across prefix column pairs.
  // HRS fixed at 9mm (fits up to 8-char decimal hour values); COST gets the rest.
  const prefixAreaW = USABLE - DESC_W - TOTAL_COL_SUM;
  const pairW = prefixes.length > 0 ? prefixAreaW / prefixes.length : prefixAreaW;
  const HRS_W  = 9;
  const COST_W = Math.round((pairW - HRS_W) * 10) / 10;

  // Flat array of column widths: [desc, hrs, cost, hrs, cost, ..., H..R×11]
  const colWidths = [DESC_W];
  for (let i = 0; i < prefixes.length; i++) colWidths.push(HRS_W, COST_W);
  TOTAL_COL_WIDTHS.forEach(w => colWidths.push(w));

  const NUM_TOTAL_COLS = TOTAL_COL_WIDTHS.length; // 11

  const TOTAL_COL_LABELS = [
    "EST'D\nHOURS", "ACT.\nHOURS", "HRS\nVAR.",
    "CONTRACT\nREV. BUD.", "ACT. REV.\nTO DATE",
    "EST'D\nCOST", "ACT COST\nTO DATE", "ACT COST\nINCL POs",
    "%\nSPENT", "PROFIT\nor LOSS", "EST'D\nADD'L BILL.",
  ];

  // ── Header section background colours ─────────────────────────────────────
  const PFXC = [220, 233, 252]; // light blue — per-prefix column pairs
  const TOTC = [200, 218, 245]; // slightly deeper blue — total columns
  const DESC = [238, 242, 252]; // near-white tint — description column

  // ── 3-row header ───────────────────────────────────────────────────────────
  // Row 1: prefix codes + TOTAL label with section background colours
  const headRow1 = [{ content: '', styles: { fillColor: DESC } }];
  for (const pfx of prefixes) {
    headRow1.push({ content: pfx, colSpan: 2, styles: { halign: 'center', fontStyle: 'bold', fillColor: PFXC } });
  }
  headRow1.push({ content: 'TOTAL', colSpan: NUM_TOTAL_COLS, styles: { halign: 'center', fontStyle: 'bold', fillColor: TOTC } });

  // Row 2: company names with matching section colours
  const headRow2 = [{ content: '', styles: { fillColor: DESC } }];
  for (const pfx of prefixes) {
    const name = prefixMeta[pfx].divisionName || pfx;
    headRow2.push({ content: name, colSpan: 2, styles: { halign: 'center', fontSize: 5, fillColor: PFXC } });
  }
  headRow2.push({ content: '', colSpan: NUM_TOTAL_COLS, styles: { fillColor: TOTC } });

  // Row 3: column-level labels with matching section colours — all numeric columns centered
  const headRow3 = [{ content: 'PROJECT /\nDESCRIPTION', styles: { halign: 'left', fontStyle: 'bold', fillColor: DESC } }];
  for (let i = 0; i < prefixes.length; i++) {
    headRow3.push(
      { content: 'ACT.\nHRS',        styles: { halign: 'center', fillColor: PFXC } },
      { content: 'ACT COST\nTO DATE', styles: { halign: 'center', fillColor: PFXC } }
    );
  }
  TOTAL_COL_LABELS.forEach(label => {
    headRow3.push({ content: label, styles: { halign: 'center', fillColor: TOTC } });
  });

  // ── Helpers ────────────────────────────────────────────────────────────────
  const fmtH = (v) => {
    const n = parseFloat(v) || 0;
    if (n === 0) return '';
    return Number.isInteger(n) ? String(n) : n.toFixed(1);
  };
  const fmtC = (v) => {
    const n = parseFloat(v) || 0;
    if (n === 0) return '';
    return n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };
  const fmtP = (o, m) => {
    if (!m || m === 0) return '';
    return ((o / m) * 100).toFixed(1) + '%';
  };

  // Build a flat autoTable row from an activity/totals data object
  const buildRow = (data, label, bold) => {
    const s = bold ? { fontStyle: 'bold' } : {};
    const sR = bold ? { fontStyle: 'bold', halign: 'right' } : { halign: 'right' };
    const cells = [bold ? { content: label, styles: s } : label];
    for (const pfx of prefixes) {
      const pp = data.perPrefix?.[pfx] || { hoursActual: 0, costActual: 0 };
      cells.push(
        { content: fmtH(pp.hoursActual), styles: sR },
        { content: fmtC(pp.costActual),  styles: sR }
      );
    }
    const h = data.hoursEstd       || 0;
    const i = data.hoursActual     || 0;
    const k = data.colK            || 0;
    const l = data.billing         || 0;
    const m = data.costEstd        || 0;
    const n = data.costActualTotal || 0;
    const o = data.totalAndPO      || 0;
    [fmtH(h), fmtH(i), fmtH(h - i), fmtC(k), fmtC(l), fmtC(m), fmtC(n), fmtC(o),
     fmtP(o, m), fmtC(l - o), fmtC(k - l)
    ].forEach(v => cells.push({ content: v, styles: sR }));
    return cells;
  };

  // ── Body helpers ───────────────────────────────────────────────────────────
  const numCols = 1 + prefixes.length * 2 + NUM_TOTAL_COLS;

  const grandTotals = {
    perPrefix: Object.fromEntries(prefixes.map(p => [p, { hoursActual: 0, costActual: 0 }])),
    hoursEstd: 0, hoursActual: 0, colK: 0, billing: 0,
    costEstd: 0, costActualTotal: 0, totalAndPO: 0,
  };

  // Build the body rows for a single discipline (returns array of rows)
  const buildDiscBody = (disc) => {
    const rows = [];
    rows.push([{
      content: disc.disciplineName || disc.disciplineCode,
      colSpan: numCols,
      styles: { fontStyle: 'bold', fontSize: FONT_SIZE + 0.5, fillColor: [242, 242, 242] },
    }]);

    for (const phase of disc.phases) {
      rows.push([{
        content: `${phase.phaseCode} - ${phase.phaseDescription}`,
        colSpan: numCols,
        styles: { fontStyle: 'bold', fillColor: false },
      }]);

      const phaseTotals = {
        perPrefix: Object.fromEntries(prefixes.map(p => [p, { hoursActual: 0, costActual: 0 }])),
        hoursEstd: 0, hoursActual: 0, colK: 0, billing: 0,
        costEstd: 0, costActualTotal: 0, totalAndPO: 0,
      };

      for (const act of phase.activities) {
        rows.push(buildRow(act, `${act.activityCode} - ${act.description}`, false));
        for (const pfx of prefixes) {
          phaseTotals.perPrefix[pfx].hoursActual += act.perPrefix[pfx]?.hoursActual || 0;
          phaseTotals.perPrefix[pfx].costActual  += act.perPrefix[pfx]?.costActual  || 0;
        }
        phaseTotals.hoursEstd       += act.hoursEstd;
        phaseTotals.hoursActual     += act.hoursActual;
        phaseTotals.colK            += act.colK;
        phaseTotals.billing         += act.billing;
        phaseTotals.costEstd        += act.costEstd;
        phaseTotals.costActualTotal += act.costActualTotal;
        phaseTotals.totalAndPO      += act.totalAndPO;
      }

      // Phase total row
      const phaseTotalRow = buildRow(phaseTotals, `${phase.phaseDescription} Total`, true);
      rows.push(phaseTotalRow.map(cell => {
        if (typeof cell === 'string') return { content: cell, styles: { fillColor: [232, 232, 232] } };
        return { ...cell, styles: { ...cell.styles, fillColor: [232, 232, 232] } };
      }));

      // Spacer row
      rows.push([{ content: '', colSpan: numCols, styles: { minCellHeight: 2, fillColor: false } }]);

      // Accumulate grand totals
      for (const pfx of prefixes) {
        grandTotals.perPrefix[pfx].hoursActual += phaseTotals.perPrefix[pfx].hoursActual;
        grandTotals.perPrefix[pfx].costActual  += phaseTotals.perPrefix[pfx].costActual;
      }
      grandTotals.hoursEstd       += phaseTotals.hoursEstd;
      grandTotals.hoursActual     += phaseTotals.hoursActual;
      grandTotals.colK            += phaseTotals.colK;
      grandTotals.billing         += phaseTotals.billing;
      grandTotals.costEstd        += phaseTotals.costEstd;
      grandTotals.costActualTotal += phaseTotals.costActualTotal;
      grandTotals.totalAndPO      += phaseTotals.totalAndPO;
    }
    return rows;
  };

  // ── Page title + vertical offsets ──────────────────────────────────────────
  // Page 1 hosts the compact letterhead band; title and table start below the
  // returned letterheadY (contentStartBelowLetterhead). Continuation pages have
  // no letterhead and use the compact top. When the letterhead already carries
  // the job/client line, the centered title stays short to avoid duplication.
  const { titleY, firstPageTop: FIRST_PAGE_TOP, contTop: CONT_TOP } =
    contentStartBelowLetterhead(options.letterheadY, { margin: MARGIN });
  const hasLetterhead = options.letterheadY != null && Number.isFinite(options.letterheadY);
  const pageTitle = hasLetterhead
    ? 'Consolidated Report'
    : `${project.jobNumber || ''} — ${project.jobName || ''} — Consolidated Report`;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(pageTitle, PAGE_W / 2, titleY, { align: 'center' });
  const currencyExtra = drawConsolidatedCurrencyLine(
    doc,
    consolidatedCurrencyLine(project, prefixes, prefixMeta),
    titleY,
    PAGE_W,
  );

  // ── Column styles for autoTable ────────────────────────────────────────────
  const columnStyles = {};
  colWidths.forEach((w, idx) => {
    columnStyles[idx] = { cellWidth: w, halign: idx === 0 ? 'left' : 'right' };
  });

  // ── Pre-compute x positions for manual border drawing ──────────────────────
  const colRightEdges = [];
  { let x = MARGIN; for (const w of colWidths) { x += w; colRightEdges.push(x); } }

  const groupBoundaryX = [];
  { let x = MARGIN + DESC_W;
    for (let i = 0; i < prefixes.length; i++) {
      if (i > 0) groupBoundaryX.push({ x, type: 'prefix' });
      x += HRS_W + COST_W;
    }
    groupBoundaryX.push({ x, type: 'total' });
  }

  // ── Shared autoTable config (reused for every discipline call) ─────────────
  const BOTTOM_MARGIN = fxFootnote ? 18 : 14;
  // margin.top applies when autoTable breaks onto a new page (no letterhead there)
  const TABLE_TOP_MARGIN = CONT_TOP;

  const sharedTableOpts = {
    head: [headRow1, headRow2, headRow3],
    theme: 'plain',
    showHead: 'everyPage',
    styles: {
      fontSize: 5.5,
      cellPadding: { top: 0.4, right: 0.6, bottom: 0.4, left: 0.6 },
      overflow: 'linebreak',
      lineWidth: 0,
      textColor: [0, 0, 0],
      valign: 'middle',
    },
    headStyles: {
      fontStyle: 'normal',
      fontSize: FONT_SIZE,
      fillColor: [255, 255, 255],
      textColor: [0, 0, 80],
      halign: 'center',
      valign: 'middle',
      lineWidth: 0,
      cellPadding: { top: 1.1, right: 0.6, bottom: 1.1, left: 0.6 },
    },
    columnStyles,
    margin: { left: MARGIN, right: MARGIN, top: TABLE_TOP_MARGIN, bottom: BOTTOM_MARGIN },
    tableWidth: USABLE,
    didDrawCell: (data) => {
      const cx = data.cell.x, cy = data.cell.y, cw = data.cell.width, ch = data.cell.height;

      // (1) Regular right-edge line
      const isHeadCell = data.cell.section === 'head';
      doc.setDrawColor(...(isHeadCell ? [80, 100, 160] : [160, 160, 160]));
      doc.setLineWidth(isHeadCell ? 0.25 : 0.18);
      doc.line(cx + cw, cy, cx + cw, cy + ch);

      // (2) Left edge for first column
      if (data.column.index === 0) {
        doc.setDrawColor(160, 160, 160);
        doc.setLineWidth(0.18);
        doc.line(cx, cy, cx, cy + ch);
      }

      // (3) Top border on first header row
      if (data.cell.section === 'head' && data.row.index === 0) {
        doc.setDrawColor(160, 160, 160);
        doc.setLineWidth(0.18);
        doc.line(cx, cy, cx + cw, cy);
      }

      // (4) Dark bottom border under last header row
      if (data.cell.section === 'head' && data.row.index === 2) {
        doc.setDrawColor(30, 30, 90);
        doc.setLineWidth(0.4);
        doc.line(cx, cy + ch, cx + cw, cy + ch);
      }

      // (5) Internal column lines for colSpan cells
      if (data.cell.colSpan > 1) {
        const isHeadLabelRow = data.cell.section === 'head' && data.row.index === 2;
        const isBody = data.cell.section === 'body';
        if (isBody || isHeadLabelRow) {
          doc.setDrawColor(...(isHeadLabelRow ? [80, 100, 160] : [160, 160, 160]));
          doc.setLineWidth(isHeadLabelRow ? 0.3 : 0.18);
          const cellRight = cx + cw;
          for (let i = 0; i < colRightEdges.length - 1; i++) {
            const ex = colRightEdges[i];
            if (ex > cx + 0.1 && ex < cellRight - 0.1) {
              doc.line(ex, cy, ex, cy + ch);
            }
          }
        }
      }

      // (6) Group boundary lines
      for (const { x, type } of groupBoundaryX) {
        const inCell = x + 0.05 > cx && x < cx + cw - 0.05;
        if (inCell) {
          const inHead = data.cell.section === 'head';
          if (type === 'total') {
            doc.setDrawColor(0, 0, 60);
            doc.setLineWidth(inHead ? 1.0 : 0.6);
          } else {
            doc.setDrawColor(0, 0, 80);
            doc.setLineWidth(inHead ? 0.8 : 0.45);
          }
          doc.line(x, cy, x, cy + ch);
        }
      }
    },
    // Use document-level page number so "continued" appears on every page after page 1
    didDrawPage: () => {
      if (doc.internal.getCurrentPageInfo().pageNumber > 1) {
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.text(
          `${project.jobNumber || ''} — ${project.jobName || ''} — Consolidated Report (continued)`,
          PAGE_W / 2, MARGIN + 2, { align: 'center' }
        );
      }
    },
  };

  // ── Render each discipline on its own page ─────────────────────────────────
  // First discipline starts on the current (first) page, below the letterhead.
  // Every subsequent discipline forces a new page so sections never break mid-page.
  let currentY = FIRST_PAGE_TOP + currencyExtra;

  for (let di = 0; di < disciplines.length; di++) {
    if (di > 0) {
      // Each new discipline starts fresh on a new page
      doc.addPage();
      currentY = TABLE_TOP_MARGIN;
    }

    const discBody = buildDiscBody(disciplines[di]);
    autoTable(doc, { ...sharedTableOpts, startY: currentY, body: discBody });
    currentY = doc.lastAutoTable.finalY + 2;
  }

  // ── TOTAL PROJECT row — append after last discipline (new page if tight) ───
  const grandRow = buildRow(grandTotals, 'TOTAL PROJECT', true).map(cell => {
    if (typeof cell === 'string') return { content: cell, styles: { fillColor: [210, 225, 242] } };
    return { ...cell, styles: { ...cell.styles, fillColor: [210, 225, 242] } };
  });

  if (PAGE_H - currentY - BOTTOM_MARGIN < 20) {
    doc.addPage();
    currentY = TABLE_TOP_MARGIN;
  }
  autoTable(doc, {
    ...sharedTableOpts,
    startY: currentY,
    showHead: 'never',
    body: [grandRow],
  });

  // ── Page footers ───────────────────────────────────────────────────────────
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.2);
    const footerLineY = fxFootnote ? PAGE_H - 16 : PAGE_H - 12;
    doc.line(MARGIN, footerLineY, PAGE_W - MARGIN, footerLineY);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(120, 120, 120);
    doc.text(`Page ${i} of ${pageCount}`, PAGE_W / 2, footerLineY + 4, { align: 'center' });
    doc.text(`${dateStr}, ${timeStr}`, PAGE_W - MARGIN, footerLineY + 4, { align: 'right' });
    if (fxFootnote) {
      doc.setFontSize(6.5);
      doc.setFont('helvetica', 'italic');
      doc.text(fxFootnote, MARGIN, footerLineY + 8);
    }
    doc.setTextColor(0, 0, 0);
  }
};

/**
 * Generate a single consolidated Report 9091 PDF.
 * All companies are merged into one, with foreign-currency values converted
 * to the master project's currency before summing.
 */
const generateConsolidated9091Report = async (reportData, branding = {}) => {
  const consolidated = consolidateReportData(reportData);
  const { project, companies } = consolidated;
  if (!companies || companies.length === 0) return;
  const company = companies[0];
  if (!company.disciplines || company.disciplines.length === 0) return;

  const now = new Date();
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });
  const { y: letterheadY } = await applyLetterhead(doc, {
    ...branding,
    jobNumber: project.jobNumber,
    jobName: project.jobName,
    margin: 10,
  });
  renderCompanyToPdf(doc, project, company, now, {}, { reportTitle: 'Consolidated Report', letterheadY });

  doc.save(exportFilename({ job: project.jobNumber, reportSlug: 'Consolidated_Report_9091', ext: 'pdf' }));
};

/**
 * Orchestrate the new consolidated summary PDF (18-column Excel-matching layout).
 *
 * Uses the compact letterhead band (slim logo + job line) on page 1 — the
 * full-height band crushed this dense grid (Domtar 61080 regression). The
 * renderer starts the title and table below the returned letterheadY.
 */
const generateConsolidatedSummaryReport = async (reportData, branding = {}) => {
  const summaryData = buildConsolidatedSummaryData(reportData);
  if (!summaryData.disciplines || summaryData.disciplines.length === 0) return;

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });
  const { y: letterheadY } = await applyLetterhead(doc, {
    ...branding,
    jobNumber: reportData.project?.jobNumber,
    jobName: reportData.project?.jobName,
    margin: 8,
    compact: true,
  });
  renderConsolidatedSummaryToPdf(
    doc,
    reportData.project,
    summaryData,
    reportData.exchangeRates || {},
    new Date(),
    { letterheadY }
  );
  doc.save(exportFilename({ job: reportData.project?.jobNumber, reportSlug: 'Consolidated_Report', ext: 'pdf' }));
};

// ─── Roll-Up Consolidated PDF ────────────────────────────────────────────────

/**
 * Aggregate reportData into a flat phase → activity structure (no discipline
 * level) for the Roll-Up consolidated report. Phases from ALL disciplines are
 * merged together under a common phaseRaw key so that, for example,
 * "00005-CONTRACT EXPENSES" from MWR and PIP are summed into one row.
 *
 * Returns:
 *   {
 *     prefixes: string[],
 *     prefixMeta: { [pfx]: { divisionName, countryCode } },
 *     phases: [
 *       { phaseCode, phaseDescription, phaseRaw,
 *         activities: [{ activityCode, description, perPrefix,
 *                         hoursEstd, hoursActual, colK, billing,
 *                         costEstd, costActualTotal, totalAndPO }] }
 *     ]
 *   }
 */
const buildRollupSummaryData = (reportData) => {
  const { project, companies, exchangeRates = {} } = reportData;
  const masterCountry = project.masterCountry || null;

  const buildConv = (company) => {
    const needsConv = !!(
      masterCountry && company.countryCode && masterCountry !== company.countryCode
    );
    if (!needsConv) return (key, v) => parseFloat(v) || 0;
    const rateKey = `${company.countryCode}_${masterCountry}`;
    const rate = exchangeRates[rateKey]?.rate || 1;
    return (key, v) => {
      const n = parseFloat(v) || 0;
      return SUMMARY_FINANCIAL_KEYS.has(key) ? n * rate : n;
    };
  };

  const prefixSet = new Set();
  const prefixMeta = {};
  for (const company of companies) {
    prefixSet.add(company.prefix);
    prefixMeta[company.prefix] = {
      divisionName: company.divisionName,
      countryCode: company.countryCode,
    };
  }
  const prefixes = Array.from(prefixSet).sort();

  // phaseRaw → { phaseCode, phaseDescription, phaseRaw, byActivity: { actKey → entry } }
  const byPhase = {};

  for (const company of companies) {
    const conv = buildConv(company);
    const pfx = company.prefix;

    for (const disc of company.disciplines) {
      for (const phase of disc.phases) {
        if (!byPhase[phase.phaseRaw]) {
          byPhase[phase.phaseRaw] = {
            phaseCode: phase.phaseCode,
            phaseDescription: phase.phaseDescription,
            phaseRaw: phase.phaseRaw,
            byActivity: {},
          };
        }
        const phaseEntry = byPhase[phase.phaseRaw];

        for (const act of phase.activities) {
          if (EXCLUDED_ACTIVITY_CODES.has(act.activityCode)) continue;

          const isLaborActivity = act.groupCol != null
            ? act.groupCol === IS_LABOR_GROUP
            : LABOR_PHASE_RE.test(phase.phaseDescription);

          const actKey = `${act.activityCode}||${act.description}`;
          if (!phaseEntry.byActivity[actKey]) {
            const perPrefix = {};
            for (const p of prefixes) perPrefix[p] = { hoursActual: 0, costActual: 0 };
            phaseEntry.byActivity[actKey] = {
              activityCode: act.activityCode,
              description: act.description,
              perPrefix,
              hoursEstd: 0,
              hoursActual: 0,
              colK: 0,
              billing: 0,
              costEstd: 0,
              costActualTotal: 0,
              totalAndPO: 0,
            };
          }
          const entry = phaseEntry.byActivity[actKey];

          if (isLaborActivity) {
            entry.perPrefix[pfx].hoursActual += parseFloat(act.hoursActual) || 0;
          }
          entry.perPrefix[pfx].costActual += conv('actualPlusAccrued', act.actualPlusAccrued);

          if (isLaborActivity) {
            entry.hoursEstd   += parseFloat(act.hoursEstd)   || 0;
            entry.hoursActual += parseFloat(act.hoursActual) || 0;
          }
          entry.colK             += parseFloat(act.revenueEstd) || 0;
          entry.billing          += parseFloat(act.revenueActual) || 0;
          entry.costEstd         += conv('costEstd', act.costEstd);
          entry.costActualTotal  += conv('actualPlusAccrued', act.actualPlusAccrued);
          entry.totalAndPO       += conv('totalCostCommitted', act.totalCostCommitted);
        }
      }
    }
  }

  const phases = Object.values(byPhase)
    .map(phase => ({
      phaseCode: phase.phaseCode,
      phaseDescription: phase.phaseDescription,
      phaseRaw: phase.phaseRaw,
      activities: Object.values(phase.byActivity)
        .sort((a, b) => a.activityCode.localeCompare(b.activityCode, undefined, { numeric: true })),
    }))
    .sort((a, b) => a.phaseCode.localeCompare(b.phaseCode, undefined, { numeric: true }));

  const activePrefixes = prefixes.filter(pfx =>
    phases.some(phase =>
      phase.activities.some(act => {
        const pp = act.perPrefix[pfx];
        return pp && (pp.hoursActual !== 0 || pp.costActual !== 0);
      })
    )
  );

  return { prefixes: activePrefixes, prefixMeta, phases };
};

/**
 * Render the Roll-Up consolidated PDF.
 * Identical column layout to the By-Discipline renderer but phases are merged
 * across all disciplines and grouped under CONTRACT / EXTRAS / REVENUE headers
 * in a single continuous autoTable.
 *
 * Phase code bucketing (parsed as integer):
 *   CONTRACT : code < 80000  (00xxx expenses, 01xxx support, 21-27xxx direct labour)
 *   EXTRAS   : 80000 ≤ code < 90000  (81-87xxx discipline extras)
 *   REVENUE  : code ≥ 90000  (90xxx revenues — aggregated as one REVENUE row)
 */
const renderRollupSummaryToPdf = (doc, project, rollupData, exchangeRates, now, options = {}) => {
  const { prefixes, prefixMeta, phases } = rollupData;
  const masterCountry = project.masterCountry || null;
  const PAGE_W = doc.internal.pageSize.getWidth();
  const PAGE_H = doc.internal.pageSize.getHeight();
  const MARGIN = 7;
  const USABLE = PAGE_W - MARGIN * 2;
  const FONT_SIZE = 6;

  const dateStr = now.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const timeStr = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

  let fxFootnote = '';
  for (const pfx of prefixes) {
    const meta = prefixMeta[pfx];
    if (masterCountry && meta.countryCode && meta.countryCode !== masterCountry) {
      const rateKey = `${meta.countryCode}_${masterCountry}`;
      const rate = exchangeRates[rateKey]?.rate;
      if (rate) {
        fxFootnote = `* ${meta.countryCode} cost values converted to ${masterCountry} at 1 ${meta.countryCode} = ${rate.toFixed(6)} ${masterCountry}`;
        break;
      }
    }
  }

  // Column widths — identical to By-Discipline renderer
  const TOTAL_COL_WIDTHS = [10, 10, 10, 15, 14, 14, 14, 14, 9, 14, 13];
  const TOTAL_COL_SUM = TOTAL_COL_WIDTHS.reduce((a, b) => a + b, 0);
  const DESC_W = 42;
  const prefixAreaW = USABLE - DESC_W - TOTAL_COL_SUM;
  const pairW = prefixes.length > 0 ? prefixAreaW / prefixes.length : prefixAreaW;
  const HRS_W  = 9;
  const COST_W = Math.round((pairW - HRS_W) * 10) / 10;

  const colWidths = [DESC_W];
  for (let i = 0; i < prefixes.length; i++) colWidths.push(HRS_W, COST_W);
  TOTAL_COL_WIDTHS.forEach(w => colWidths.push(w));

  const NUM_TOTAL_COLS = TOTAL_COL_WIDTHS.length;

  const TOTAL_COL_LABELS = [
    "EST'D\nHOURS", "ACT.\nHOURS", "HRS\nVAR.",
    "CONTRACT\nREV. BUD.", "ACT. REV.\nTO DATE",
    "EST'D\nCOST", "ACT COST\nTO DATE", "ACT COST\nINCL POs",
    "%\nSPENT", "PROFIT\nor LOSS", "EST'D\nADD'L BILL.",
  ];

  const PFXC = [220, 233, 252];
  const TOTC = [200, 218, 245];
  const DESC = [238, 242, 252];

  // 3-row header — identical to By-Discipline renderer
  const headRow1 = [{ content: '', styles: { fillColor: DESC } }];
  for (const pfx of prefixes) {
    headRow1.push({ content: pfx, colSpan: 2, styles: { halign: 'center', fontStyle: 'bold', fillColor: PFXC } });
  }
  headRow1.push({ content: 'TOTAL', colSpan: NUM_TOTAL_COLS, styles: { halign: 'center', fontStyle: 'bold', fillColor: TOTC } });

  const headRow2 = [{ content: '', styles: { fillColor: DESC } }];
  for (const pfx of prefixes) {
    const name = prefixMeta[pfx].divisionName || pfx;
    headRow2.push({ content: name, colSpan: 2, styles: { halign: 'center', fontSize: 5, fillColor: PFXC } });
  }
  headRow2.push({ content: '', colSpan: NUM_TOTAL_COLS, styles: { fillColor: TOTC } });

  const headRow3 = [{ content: 'PROJECT /\nDESCRIPTION', styles: { halign: 'left', fontStyle: 'bold', fillColor: DESC } }];
  for (let i = 0; i < prefixes.length; i++) {
    headRow3.push(
      { content: 'ACT.\nHRS',         styles: { halign: 'center', fillColor: PFXC } },
      { content: 'ACT COST\nTO DATE', styles: { halign: 'center', fillColor: PFXC } }
    );
  }
  TOTAL_COL_LABELS.forEach(label => {
    headRow3.push({ content: label, styles: { halign: 'center', fillColor: TOTC } });
  });

  const fmtH = (v) => {
    const n = parseFloat(v) || 0;
    if (n === 0) return '';
    return Number.isInteger(n) ? String(n) : n.toFixed(1);
  };
  const fmtC = (v) => {
    const n = parseFloat(v) || 0;
    if (n === 0) return '';
    return n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };
  const fmtP = (o, m) => {
    if (!m || m === 0) return '';
    return ((o / m) * 100).toFixed(1) + '%';
  };

  const numCols = 1 + prefixes.length * 2 + NUM_TOTAL_COLS;

  const buildRow = (data, label, bold) => {
    const s  = bold ? { fontStyle: 'bold' } : {};
    const sR = bold ? { fontStyle: 'bold', halign: 'right' } : { halign: 'right' };
    const cells = [bold ? { content: label, styles: s } : label];
    for (const pfx of prefixes) {
      const pp = data.perPrefix?.[pfx] || { hoursActual: 0, costActual: 0 };
      cells.push(
        { content: fmtH(pp.hoursActual), styles: sR },
        { content: fmtC(pp.costActual),  styles: sR }
      );
    }
    const h = data.hoursEstd       || 0;
    const i = data.hoursActual     || 0;
    const k = data.colK            || 0;
    const l = data.billing         || 0;
    const m = data.costEstd        || 0;
    const n = data.costActualTotal || 0;
    const o = data.totalAndPO      || 0;
    [fmtH(h), fmtH(i), fmtH(h - i), fmtC(k), fmtC(l), fmtC(m), fmtC(n), fmtC(o),
     fmtP(o, m), fmtC(l - o), fmtC(k - l)
    ].forEach(v => cells.push({ content: v, styles: sR }));
    return cells;
  };

  const makeAccum = () => ({
    perPrefix: Object.fromEntries(prefixes.map(p => [p, { hoursActual: 0, costActual: 0 }])),
    hoursEstd: 0, hoursActual: 0, colK: 0, billing: 0,
    costEstd: 0, costActualTotal: 0, totalAndPO: 0,
  });

  const addToAccum = (accum, src) => {
    for (const pfx of prefixes) {
      accum.perPrefix[pfx].hoursActual += src.perPrefix?.[pfx]?.hoursActual || 0;
      accum.perPrefix[pfx].costActual  += src.perPrefix?.[pfx]?.costActual  || 0;
    }
    accum.hoursEstd       += src.hoursEstd       || 0;
    accum.hoursActual     += src.hoursActual     || 0;
    accum.colK            += src.colK            || 0;
    accum.billing         += src.billing         || 0;
    accum.costEstd        += src.costEstd        || 0;
    accum.costActualTotal += src.costActualTotal || 0;
    accum.totalAndPO      += src.totalAndPO      || 0;
  };

  const shadeRow = (row, bg) => row.map(cell => {
    if (typeof cell === 'string') return { content: cell, styles: { fillColor: bg } };
    return { ...cell, styles: { ...(cell.styles || {}), fillColor: bg } };
  });

  // Bucket phases by code range
  const contractPhases = phases.filter(p => parseInt(p.phaseCode, 10) < 80000);
  const extrasPhases   = phases.filter(p => {
    const c = parseInt(p.phaseCode, 10);
    return c >= 80000 && c < 90000;
  });
  const revenuePhases  = phases.filter(p => parseInt(p.phaseCode, 10) >= 90000);

  const extrasTotals          = makeAccum();
  const contractAndExtrasTotals = makeAccum();
  const revenueTotals         = makeAccum();

  const body = [];

  // Job number section header
  body.push([{
    content: project.jobNumber || '',
    colSpan: numCols,
    styles: { fontStyle: 'bold', fontSize: FONT_SIZE + 1, fillColor: [242, 242, 242] },
  }]);

  // CONTRACT & EXTRAS section header
  body.push([{
    content: 'CONTRACT & EXTRAS',
    colSpan: numCols,
    styles: { fontStyle: 'bold', fillColor: [225, 232, 246] },
  }]);

  // CONTRACT sub-header
  body.push([{
    content: 'CONTRACT',
    colSpan: numCols,
    styles: { fontStyle: 'bold', fillColor: false },
  }]);

  for (const phase of contractPhases) {
    // Discipline-specific direct labour phases (21xxx–27xxx) get a green tint so
    // each discipline's labour section is easy to spot in the flat stream.
    const isDirectLabour = parseInt(phase.phaseCode, 10) >= 21000;
    const phaseHdrFill = isDirectLabour ? [210, 238, 218] : false;
    body.push([{
      content: `${phase.phaseCode} - ${phase.phaseDescription}`,
      colSpan: numCols,
      styles: { fontStyle: 'bold', fillColor: phaseHdrFill },
    }]);

    const phaseTotals = makeAccum();
    for (const act of phase.activities) {
      body.push(buildRow(act, `${act.activityCode} - ${act.description}`, false));
      addToAccum(phaseTotals, act);
    }

    body.push(shadeRow(
      buildRow(phaseTotals, `${phase.phaseDescription} Total`, true),
      [232, 232, 232]
    ));
    body.push([{ content: '', colSpan: numCols, styles: { minCellHeight: 2, fillColor: false } }]);

    addToAccum(contractAndExtrasTotals, phaseTotals);
  }

  // EXTRAS sub-header
  body.push([{
    content: 'EXTRAS',
    colSpan: numCols,
    styles: { fontStyle: 'bold', fillColor: [225, 232, 246] },
  }]);

  for (const phase of extrasPhases) {
    // Each extras phase represents one discipline's change-order work — amber tint
    // makes the start of each discipline's extras block immediately recognisable.
    body.push([{
      content: `${phase.phaseCode} - ${phase.phaseDescription}`,
      colSpan: numCols,
      styles: { fontStyle: 'bold', fillColor: [252, 238, 210] },
    }]);

    const phaseTotals = makeAccum();
    for (const act of phase.activities) {
      body.push(buildRow(act, `${act.activityCode} - ${act.description}`, false));
      addToAccum(phaseTotals, act);
    }

    body.push(shadeRow(
      buildRow(phaseTotals, `${phase.phaseDescription} Total`, true),
      [232, 232, 232]
    ));
    body.push([{ content: '', colSpan: numCols, styles: { minCellHeight: 2, fillColor: false } }]);

    addToAccum(extrasTotals, phaseTotals);
    addToAccum(contractAndExtrasTotals, phaseTotals);
  }

  // EXTRAS Total row
  body.push(shadeRow(buildRow(extrasTotals, 'EXTRAS Total', true), [215, 228, 248]));

  // CONTRACT & EXTRAS Total row
  body.push(shadeRow(buildRow(contractAndExtrasTotals, 'CONTRACT & EXTRAS Total', true), [215, 228, 248]));

  // REVENUE — aggregate all 90xxx activities into one summary row
  for (const phase of revenuePhases) {
    for (const act of phase.activities) {
      addToAccum(revenueTotals, act);
    }
  }
  body.push(shadeRow(buildRow(revenueTotals, 'REVENUE', true), [215, 228, 248]));

  // Grand Total
  const grandTotals = makeAccum();
  addToAccum(grandTotals, contractAndExtrasTotals);
  addToAccum(grandTotals, revenueTotals);
  body.push(shadeRow(
    buildRow(grandTotals, `${project.jobNumber || ''} Total`, true),
    [210, 225, 242]
  ));

  // Column styles
  const columnStyles = {};
  colWidths.forEach((w, idx) => {
    columnStyles[idx] = { cellWidth: w, halign: idx === 0 ? 'left' : 'right' };
  });

  // Pre-compute positions for manual border drawing
  const colRightEdges = [];
  { let x = MARGIN; for (const w of colWidths) { x += w; colRightEdges.push(x); } }

  const groupBoundaryX = [];
  { let x = MARGIN + DESC_W;
    for (let i = 0; i < prefixes.length; i++) {
      if (i > 0) groupBoundaryX.push({ x, type: 'prefix' });
      x += HRS_W + COST_W;
    }
    groupBoundaryX.push({ x, type: 'total' });
  }

  const BOTTOM_MARGIN = fxFootnote ? 18 : 14;
  const { titleY, firstPageTop: FIRST_PAGE_TOP, contTop: CONT_TOP } =
    contentStartBelowLetterhead(options.letterheadY, { margin: MARGIN });
  const TABLE_TOP_MARGIN = CONT_TOP;
  const hasLetterhead = options.letterheadY != null && Number.isFinite(options.letterheadY);
  const pageTitle = hasLetterhead
    ? 'Roll-Up Consolidated Report'
    : `${project.jobNumber || ''} — ${project.jobName || ''} — Roll-Up Consolidated Report`;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(pageTitle, PAGE_W / 2, titleY, { align: 'center' });
  const currencyExtra = drawConsolidatedCurrencyLine(
    doc,
    consolidatedCurrencyLine(project, prefixes, prefixMeta),
    titleY,
    PAGE_W,
  );

  autoTable(doc, {
    head: [headRow1, headRow2, headRow3],
    body,
    theme: 'plain',
    showHead: 'everyPage',
    styles: {
      fontSize: 5.5,
      cellPadding: { top: 0.4, right: 0.6, bottom: 0.4, left: 0.6 },
      overflow: 'linebreak',
      lineWidth: 0,
      textColor: [0, 0, 0],
      valign: 'middle',
    },
    headStyles: {
      fontStyle: 'normal',
      fontSize: FONT_SIZE,
      fillColor: [255, 255, 255],
      textColor: [0, 0, 80],
      halign: 'center',
      valign: 'middle',
      lineWidth: 0,
      cellPadding: { top: 1.1, right: 0.6, bottom: 1.1, left: 0.6 },
    },
    columnStyles,
    margin: { left: MARGIN, right: MARGIN, top: TABLE_TOP_MARGIN, bottom: BOTTOM_MARGIN },
    tableWidth: USABLE,
    startY: FIRST_PAGE_TOP + currencyExtra,
    didDrawCell: (data) => {
      const cx = data.cell.x, cy = data.cell.y, cw = data.cell.width, ch = data.cell.height;
      const isHeadCell = data.cell.section === 'head';
      doc.setDrawColor(...(isHeadCell ? [80, 100, 160] : [160, 160, 160]));
      doc.setLineWidth(isHeadCell ? 0.25 : 0.18);
      doc.line(cx + cw, cy, cx + cw, cy + ch);

      if (data.column.index === 0) {
        doc.setDrawColor(160, 160, 160);
        doc.setLineWidth(0.18);
        doc.line(cx, cy, cx, cy + ch);
      }

      if (data.cell.section === 'head' && data.row.index === 0) {
        doc.setDrawColor(160, 160, 160);
        doc.setLineWidth(0.18);
        doc.line(cx, cy, cx + cw, cy);
      }

      if (data.cell.section === 'head' && data.row.index === 2) {
        doc.setDrawColor(30, 30, 90);
        doc.setLineWidth(0.4);
        doc.line(cx, cy + ch, cx + cw, cy + ch);
      }

      if (data.cell.colSpan > 1) {
        const isHeadLabelRow = data.cell.section === 'head' && data.row.index === 2;
        const isBody = data.cell.section === 'body';
        if (isBody || isHeadLabelRow) {
          doc.setDrawColor(...(isHeadLabelRow ? [80, 100, 160] : [160, 160, 160]));
          doc.setLineWidth(isHeadLabelRow ? 0.3 : 0.18);
          const cellRight = cx + cw;
          for (let i = 0; i < colRightEdges.length - 1; i++) {
            const ex = colRightEdges[i];
            if (ex > cx + 0.1 && ex < cellRight - 0.1) {
              doc.line(ex, cy, ex, cy + ch);
            }
          }
        }
      }

      for (const { x, type } of groupBoundaryX) {
        const inCell = x + 0.05 > cx && x < cx + cw - 0.05;
        if (inCell) {
          const inHead = data.cell.section === 'head';
          if (type === 'total') {
            doc.setDrawColor(0, 0, 60);
            doc.setLineWidth(inHead ? 1.0 : 0.6);
          } else {
            doc.setDrawColor(0, 0, 80);
            doc.setLineWidth(inHead ? 0.8 : 0.45);
          }
          doc.line(x, cy, x, cy + ch);
        }
      }
    },
    didDrawPage: () => {
      if (doc.internal.getCurrentPageInfo().pageNumber > 1) {
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.text(
          `${project.jobNumber || ''} — ${project.jobName || ''} — Roll-Up Consolidated Report (continued)`,
          PAGE_W / 2, MARGIN + 2, { align: 'center' }
        );
      }
    },
  });

  // Page footers
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.2);
    const footerLineY = fxFootnote ? PAGE_H - 16 : PAGE_H - 12;
    doc.line(MARGIN, footerLineY, PAGE_W - MARGIN, footerLineY);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(120, 120, 120);
    doc.text(`Page ${i} of ${pageCount}`, PAGE_W / 2, footerLineY + 4, { align: 'center' });
    doc.text(`${dateStr}, ${timeStr}`, PAGE_W - MARGIN, footerLineY + 4, { align: 'right' });
    if (fxFootnote) {
      doc.setFontSize(6.5);
      doc.setFont('helvetica', 'italic');
      doc.text(fxFootnote, MARGIN, footerLineY + 8);
    }
    doc.setTextColor(0, 0, 0);
  }
};

/**
 * Generate and download the Roll-Up consolidated PDF.
 * Same compact letterhead treatment as generateConsolidatedSummaryReport.
 */
const generateRollupReport = async (reportData, branding = {}) => {
  const rollupData = buildRollupSummaryData(reportData);
  if (!rollupData.phases || rollupData.phases.length === 0) return;

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });
  const { y: letterheadY } = await applyLetterhead(doc, {
    ...branding,
    jobNumber: reportData.project?.jobNumber,
    jobName: reportData.project?.jobName,
    margin: 8,
    compact: true,
  });
  renderRollupSummaryToPdf(
    doc,
    reportData.project,
    rollupData,
    reportData.exchangeRates || {},
    new Date(),
    { letterheadY }
  );
  doc.save(exportFilename({ job: reportData.project?.jobNumber, reportSlug: 'Rollup_Report', ext: 'pdf' }));
};

// ─── End Report PDF Generators ───────────────────────────────────────────────

// ─── React-window Virtualized Table ──────────────────────────────────────────
//
// Uses react-window v2 List for vertical virtualization.
// Only ~10 rows + overscan are in the DOM at any time regardless of dataset size.
//
// Column widths are pre-computed from the full dataset via canvas.measureText()
// (useColumnWidths hook) so they never change during scroll — no reflows/flashing.
//
// Sticky header is a plain flex div above the List. A native scroll listener on
// the List's container element syncs the header's scrollLeft so both scroll in
// lock-step horizontally.
//
// Pass key={activeBatchId} on VirtualTable from the parent to reset scroll
// position when the batch changes.

const ROW_HEIGHT    = 29;  // px — must match cell padding + line-height
const COL_H_PADDING = 22;  // horizontal cell padding budget (10px×2 + 1px border + 1px buffer)

/**
 * Canvas-based text measurer — gives the exact pixel width the browser will
 * render for a given string at a given font. Reuses a single off-screen canvas
 * so there is no allocation overhead inside the scan loop.
 */
const _measureCanvas = (() => {
  let canvas = null;
  return (text, font) => {
    if (typeof document === 'undefined') return text.length * 8;
    if (!canvas) canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.font = font;
    return ctx.measureText(text).width;
  };
})();

const CELL_FONT   = '12px system-ui,-apple-system,Segoe UI,sans-serif';
const HEADER_FONT = '600 11px system-ui,-apple-system,Segoe UI,sans-serif';

/**
 * Compute stable auto-fit column widths from the full dataset using canvas
 * measurement. Runs once per data/columns reference via useMemo — zero cost
 * during scroll. Only the passed columns are measured.
 */
const useColumnWidths = (data, columns) =>
  React.useMemo(() => {
    const widths = {};
    columns.forEach(col => {
      widths[col.key] = Math.ceil(_measureCanvas(col.label, HEADER_FONT)) + COL_H_PADDING;
    });
    columns.forEach(col => {
      if (col.type === 'text')         widths[col.key] = Math.max(widths[col.key], 80);
      else if (col.type === 'percent') widths[col.key] = Math.max(widths[col.key], 88);
      else                             widths[col.key] = Math.max(widths[col.key], 98);
    });
    if (data.length === 0) return widths;
    const textCols = columns.filter(c => c.type === 'text');
    for (const row of data) {
      for (const col of textCols) {
        const rendered = formatValue(row[col.key], col.type);
        if (rendered === '-') continue;
        const w = Math.ceil(_measureCanvas(rendered, CELL_FONT)) + COL_H_PADDING;
        if (w > widths[col.key]) widths[col.key] = w;
      }
    }
    for (const col of textCols) {
      widths[col.key] = Math.min(420, widths[col.key]);
    }
    return widths;
  }, [data, columns]);

// ── react-window v2 helpers ───────────────────────────────────────────────────

/**
 * Row component for react-window v2 List.
 * react-window v2 spreads `rowProps` directly onto this component, so
 * `rows`, `colWidths`, and `totalWidth` arrive as top-level props.
 *
 * `style` from react-window has `position: absolute, width: '100%', height: ROW_HEIGHT`.
 * We override `width` to `totalWidth` so that the row extends beyond the List's
 * viewport, which triggers the List's `overflowX: auto` horizontal scrollbar.
 */
const RowComponent = React.memo(({ index, style, rows, columns, colWidths, totalWidth }) => {
  const row = rows[index];
  return (
    <div
      style={{
        ...style,
        width: totalWidth,      // override react-window's '100%' — enables horizontal scroll
        display: 'flex',
        backgroundColor: index % 2 === 0 ? '#fff' : '#f9fafb',
        borderBottom: '1px solid #f0f0f0',
        boxSizing: 'border-box',
      }}
    >
      {columns.map((col, ci) => (
        <div
          key={col.key}
          style={{
            width: colWidths[col.key],
            minWidth: colWidths[col.key],
            flexShrink: 0,
            padding: '6px 10px',
            whiteSpace: 'nowrap',
            borderRight: ci < columns.length - 1 ? '1px solid #e5e7eb' : 'none',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: '12px',
            color: '#374151',
            textAlign: col.type === 'number' || col.type === 'percent' ? 'right' : 'left',
            boxSizing: 'border-box',
            lineHeight: '17px',
          }}
          title={formatValue(row[col.key], col.type)}
        >
          {formatValue(row[col.key], col.type)}
        </div>
      ))}
    </div>
  );
});

const VirtualTable = React.memo(({ data, columns, colWidths, onResize, loading, sortConfig, onSort, filterTemplate }) => {
  const headerRef = React.useRef(null);
  const listRef   = useListRef();
  const containerRef = React.useRef(null);
  const resizingRef  = React.useRef(null);

  const totalWidth = React.useMemo(
    () => columns.reduce((sum, col) => sum + (colWidths[col.key] || 100), 0),
    [columns, colWidths]
  );

  // ── Column resize (drag the right edge of a header cell) ──
  const handleResizeMove = React.useCallback((e) => {
    if (!resizingRef.current) return;
    const { colKey, startX, startWidth } = resizingRef.current;
    const newWidth = Math.max(60, startWidth + (e.clientX - startX));
    onResize && onResize(colKey, newWidth);
  }, [onResize]);

  const handleResizeEnd = React.useCallback(() => {
    resizingRef.current = null;
    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEnd);
  }, [handleResizeMove]);

  const handleResizeStart = React.useCallback((e, colKey) => {
    e.preventDefault();
    e.stopPropagation();
    resizingRef.current = { colKey, startX: e.clientX, startWidth: colWidths[colKey] || 100 };
    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
  }, [colWidths, handleResizeMove, handleResizeEnd]);

  React.useEffect(() => () => {
    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEnd);
  }, [handleResizeMove, handleResizeEnd]);

  // Sync header scroll with list scroll — use a polling approach to find
  // the scrollable element since react-window v2 may not expose it immediately.
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let scrollEl = null;
    const findAndBind = () => {
      // The List renders a div with overflowX: auto — find it
      const candidate = listRef.current?.element || container.querySelector('[style*="overflow"]');
      if (candidate && candidate !== scrollEl) {
        scrollEl = candidate;
        scrollEl.addEventListener('scroll', sync, { passive: true });
      }
    };

    const sync = () => {
      if (headerRef.current && scrollEl) {
        headerRef.current.scrollLeft = scrollEl.scrollLeft;
      }
    };

    findAndBind();
    // Re-check after a short delay in case the List mounts asynchronously
    const timer = setTimeout(findAndBind, 100);
    const timer2 = setTimeout(findAndBind, 500);

    return () => {
      clearTimeout(timer);
      clearTimeout(timer2);
      if (scrollEl) scrollEl.removeEventListener('scroll', sync);
    };
  }, [data, listRef]);

  const rowProps = React.useMemo(
    () => ({ rows: data, columns, colWidths, totalWidth }),
    [data, columns, colWidths, totalWidth]
  );

  const listHeight = Math.min(TABLE_MAX_HEIGHT, data.length * ROW_HEIGHT);

  return (
    <div ref={containerRef} style={{
      border: '1px solid #e5e7eb',
      borderRadius: '4px',
      overflow: 'hidden',
      opacity: loading ? 0.4 : 1,
      transition: 'opacity 0.2s',
      pointerEvents: loading ? 'none' : 'auto',
    }}>
      <div
        ref={headerRef}
        style={{ overflowX: 'hidden', overflowY: 'hidden', borderBottom: '2px solid #e2e8f0' }}
      >
        <div style={{ display: 'flex', width: totalWidth, backgroundColor: '#f8fafc' }}>
          {columns.map((col, i) => {
            const hasFilter = filterTemplate && (filterTemplate.columnFilters[col.key]?.length > 0 || filterTemplate.filterText[col.key]);
            return (
              <div
                key={col.key}
                style={{
                  width: colWidths[col.key],
                  minWidth: colWidths[col.key],
                  flexShrink: 0,
                  padding: '8px 10px',
                  whiteSpace: 'nowrap',
                  borderRight: i < columns.length - 1 ? '1px solid #d1d5db' : 'none',
                  fontWeight: '600',
                  fontSize: '11px',
                  color: '#64748b',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  textAlign: col.type === 'number' || col.type === 'percent' ? 'right' : 'left',
                  boxSizing: 'border-box',
                  cursor: 'pointer',
                  position: 'relative',
                }}
                title={col.label}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: col.type === 'number' || col.type === 'percent' ? 'flex-end' : 'flex-start' }}>
                  <span onClick={() => onSort && onSort(col.key)} style={{ flex: 1, textAlign: 'inherit' }}>
                    {col.label}
                    {sortConfig && sortConfig.key === col.key && (
                      <span style={{ marginLeft: '4px', fontSize: '10px' }}>{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>
                    )}
                  </span>
                  {filterTemplate && (
                    <span
                      onClick={(e) => filterTemplate.openFilterAtPosition(e, col.key)}
                      style={{ cursor: 'pointer', opacity: hasFilter ? 1 : 0.5, display: 'flex', flexShrink: 0 }}
                      title="Filter"
                    >
                      <FilterIcon isActive={!!hasFilter} color="#64748b" />
                    </span>
                  )}
                </div>
                {/* Resize handle — drag to adjust this column's width */}
                {onResize && (
                  <div
                    onMouseDown={(e) => handleResizeStart(e, col.key)}
                    onClick={(e) => e.stopPropagation()}
                    title="Drag to resize column"
                    style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: '6px', cursor: 'col-resize', backgroundColor: 'transparent', borderRight: '2px solid transparent' }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderRight = '2px solid #3b82f6'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderRight = '2px solid transparent'; }}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      <List
        listRef={listRef}
        rowComponent={RowComponent}
        rowCount={data.length}
        rowHeight={ROW_HEIGHT}
        rowProps={rowProps}
        overscanCount={10}
        defaultHeight={listHeight}
        style={{
          height: listHeight,
          overflowX: 'auto',
        }}
      />
    </div>
  );
});

// ─── End React-window Virtualized Table ──────────────────────────────────────

/**
 * Isolated exchange rate editor.
 * Keeps all keystroke state local so typing never triggers a parent re-render.
 * The parent only re-renders when onSave resolves (i.e. a successful save).
 */

const MaestroReports = ({ projectId, projectDetails }) => {
  const { instance, accounts } = useMsal();
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError, setDataError] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [consolidatedLoading, setConsolidatedLoading] = useState(false);
  const [consolidatedMode, setConsolidatedMode] = useState('byDiscipline'); // 'byDiscipline' | 'rollup'

  // Batch timeline state
  const [batches, setBatches] = useState([]);
  const [activeBatchId, setActiveBatchId] = useState(null);
  const [batchesLoading, setBatchesLoading] = useState(false);

  // Exchange rate state — read-only list; editing is done in Admin Panel → Accounting Settings
  const [exchangeRates, setExchangeRates] = useState([]);

  // Currency view toggle — 'native' shows each row in its company's own
  // currency; 'CAD' / 'USD' converts rows from other currencies for display.
  // Default is the master project's currency once /data returns it, unless
  // this browser already stored an explicit choice for this project.
  const [currencyView, setCurrencyView] = useState(() => loadStoredCurrencyView(projectId) || 'native');
  const currencyViewExplicitRef = useRef(!!loadStoredCurrencyView(projectId));
  // prefix (division number) → currency code, from the /data endpoint
  const [divisionCurrencies, setDivisionCurrencies] = useState({});
  // Master project currency (null when no company can be identified for the job)
  const [masterCurrency, setMasterCurrency] = useState(null);
  // How the master company was identified: { source, sourceLabel, detail,
  // divisionName, divisionNumber, conflicts[] } — from /data
  const [masterSource, setMasterSource] = useState(null);

  const chooseCurrencyView = useCallback((id) => {
    currencyViewExplicitRef.current = true;
    setCurrencyView(id);
    try {
      localStorage.setItem(`${CURRENCY_VIEW_STORAGE_PREFIX}${projectId}`, id);
    } catch { /* storage blocked */ }
  }, [projectId]);

  useEffect(() => {
    const saved = loadStoredCurrencyView(projectId);
    currencyViewExplicitRef.current = !!saved;
    setMasterCurrency(null);
    setMasterSource(null);
    setCurrencyView(saved || 'native');
  }, [projectId]);

  // Help popover state
  const [helpOpen, setHelpOpen] = useState(false);

  // ── Column personalization (visibility + order, saved per user/browser) ──
  const colOrder = useColumnOrder(COLUMN_ORDER_STORAGE_KEY, TABLE_COLUMNS);
  const [visibleColumns, setVisibleColumns] = useState(loadVisibleColumns);
  const [showColumnConfig, setShowColumnConfig] = useState(false);
  useEffect(() => { saveVisibleColumns(visibleColumns); }, [visibleColumns]);

  // Ordered + visible column definitions used by the table and the Excel export
  const visibleCols = useMemo(
    () => colOrder.orderedColumns.filter(c => visibleColumns.has(c.key)),
    [colOrder.orderedColumns, visibleColumns]
  );

  // ── Column widths: auto-fit base + manual per-column overrides (drag) ──
  const [manualWidths, setManualWidths] = useState({});
  const handleColumnResize = useCallback((colKey, width) => {
    setManualWidths(prev => ({ ...prev, [colKey]: width }));
  }, []);
  // Auto-fit button: drop all manual overrides — widths return to content-fit
  const handleAutoFit = useCallback(() => setManualWidths({}), []);

  // Excel export state
  const [exporting, setExporting] = useState(false);
  // Export menu: choose filtered (as displayed) vs complete table
  const [showExportMenu, setShowExportMenu] = useState(false);
  // Audit log modal (imports, deletes, seeds, exchange-rate changes)
  const [auditLogOpen, setAuditLogOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);

  // Sorting state — defaults to Activity; the user's choice is remembered
  // per browser user (localStorage), same as the Columns personalization.
  const [sortConfig, setSortConfig] = useState(loadStoredSort);

  const handleSort = useCallback((key) => {
    setSortConfig(prev => {
      const next = {
        key,
        direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
      };
      try { localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify(next)); } catch { /* storage blocked */ }
      return next;
    });
  }, []);

  // ── Currency view conversion ──
  // { 'CAD_USD': { rate, updatedAt }, 'USD_CAD': { ... } }
  const rateMap = useMemo(() => {
    const m = {};
    for (const r of exchangeRates) {
      m[`${r.from_currency}_${r.to_currency}`] = { rate: parseFloat(r.rate), updatedAt: r.updated_at };
    }
    return m;
  }, [exchangeRates]);

  // The rate actually in use for the active view (for the toolbar chip)
  const activeConversion = useMemo(() => {
    if (currencyView === 'native') return null;
    const from = currencyView === 'USD' ? 'CAD' : 'USD';
    const rateData = rateMap[`${from}_${currencyView}`];
    if (!rateData || !isFinite(rateData.rate)) return null;
    return { from, to: currencyView, ...rateData };
  }, [currencyView, rateMap]);

  // Rows whose company currency differs from the selected view get their
  // dollar columns converted at the current exchange rate. Quantities, hours,
  // and percentages are never converted. Filtering/sorting/export all run on
  // the converted values, so what you see is what you filter and export.
  const convertedData = useMemo(() => {
    if (currencyView === 'native') return data;
    return data.map(row => {
      const rowCurrency = divisionCurrencies[String(row.prefix || '').trim()];
      if (!rowCurrency || rowCurrency === currencyView) return row;
      const rateData = rateMap[`${rowCurrency}_${currencyView}`];
      if (!rateData || !isFinite(rateData.rate)) return row;
      const out = { ...row };
      DOLLAR_COLUMN_KEYS.forEach(key => {
        const v = out[key];
        if (v === null || v === undefined || v === '') return;
        const n = parseFloat(v);
        if (!isNaN(n)) out[key] = n * rateData.rate;
      });
      return out;
    });
  }, [data, currencyView, divisionCurrencies, rateMap]);

  // Filtering Template
  const filterTemplate = useFilterTemplate({ items: convertedData, columns: TABLE_COLUMNS });
  const activeFilters = filterTemplate.getActiveFilters();

  // Apply filtering + sorting
  const displayData = useMemo(() => {
    let result = filterTemplate.filteredItems;

    if (sortConfig.key) {
      result = [...result].sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];
        if (aVal == null && bVal == null) return 0;
        if (aVal == null) return sortConfig.direction === 'asc' ? 1 : -1;
        if (bVal == null) return sortConfig.direction === 'asc' ? -1 : 1;
        const aNum = parseFloat(aVal);
        const bNum = parseFloat(bVal);
        if (!isNaN(aNum) && !isNaN(bNum)) {
          return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
        }
        const comparison = String(aVal).localeCompare(String(bVal));
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      });
    }

    return result;
  }, [filterTemplate.filteredItems, sortConfig]);

  // Effective column widths = auto-fit base (content measurement) + manual drag overrides
  const baseWidths = useColumnWidths(displayData, visibleCols);
  const effectiveWidths = useMemo(
    () => ({ ...baseWidths, ...manualWidths }),
    [baseWidths, manualWidths]
  );

  // Job number for export filenames (falls back to the project code in the data)
  const jobNumber = projectDetails?.job_number
    || (data[0]?.project ? String(data[0].project).trim().split('-')[0] : 'project');

  /**
   * Export the dashboard table to Excel.
   * rowsScope 'filtered' (default) exports exactly what is displayed (columns,
   * order, filters, sort); 'all' exports the complete table (every row, still
   * ordered by the active sort and using the visible columns).
   */
  const handleExportExcel = useCallback(async (rowsScope = 'filtered') => {
    setShowExportMenu(false);
    let exportRows = displayData;
    if (rowsScope === 'all') {
      exportRows = sortConfig.key
        ? [...convertedData].sort((a, b) => {
            const aVal = a[sortConfig.key];
            const bVal = b[sortConfig.key];
            if (aVal == null && bVal == null) return 0;
            if (aVal == null) return sortConfig.direction === 'asc' ? 1 : -1;
            if (bVal == null) return sortConfig.direction === 'asc' ? -1 : 1;
            const aNum = parseFloat(aVal);
            const bNum = parseFloat(bVal);
            if (!isNaN(aNum) && !isNaN(bNum)) {
              return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
            }
            const comparison = String(aVal).localeCompare(String(bVal));
            return sortConfig.direction === 'asc' ? comparison : -comparison;
          })
        : convertedData;
    }
    if (exportRows.length === 0 || visibleCols.length === 0) return;
    setExporting(true);
    try {
      const ExcelJS = (await import('exceljs')).default;
      const wb = new ExcelJS.Workbook();
      wb.creator = 'LGL Tools';
      wb.created = new Date();
      const ws = wb.addWorksheet('Maestro Data', {
        views: [{ state: 'frozen', ySplit: 1 }],
      });

      // Columns mirror the on-screen layout; widths approximate on-screen pixels
      ws.columns = visibleCols.map(col => ({
        header: col.label,
        key: col.key,
        width: Math.max(10, Math.min(60, Math.round((effectiveWidths[col.key] || 100) / 7))),
      }));

      // Header styling
      const headerRow = ws.getRow(1);
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
      headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF334155' } };
      headerRow.alignment = { vertical: 'middle' };
      headerRow.height = 20;

      // Data rows — numbers stay numeric so Excel can sum/filter them
      for (const row of exportRows) {
        const excelRow = ws.addRow(visibleCols.map(col => {
          const value = row[col.key];
          if (value === null || value === undefined || value === '') return null;
          if (col.type === 'number' || col.type === 'percent') {
            const n = parseFloat(value);
            return isNaN(n) ? null : n;
          }
          return String(value).trim();
        }));
        visibleCols.forEach((col, i) => {
          if (col.type === 'number')  excelRow.getCell(i + 1).numFmt = '#,##0.00';
          if (col.type === 'percent') excelRow.getCell(i + 1).numFmt = '0.00"%"';
        });
      }

      ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: visibleCols.length } };

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = exportFilename({
        job: jobNumber,
        reportSlug: `Maestro_Dashboard${rowsScope === 'all' ? '_Complete' : ''}${currencyView === 'native' ? '' : `_${currencyView}`}`,
        ext: 'xlsx',
      });
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Maestro Excel export failed:', err);
      setError('Excel export failed: ' + (err.message || err));
    } finally {
      setExporting(false);
    }
  }, [displayData, convertedData, sortConfig, visibleCols, effectiveWidths, jobNumber, currencyView]);

  const fetchBatches = useCallback(async () => {
    if (!projectId || !accounts || accounts.length === 0) return;
    setBatchesLoading(true);
    try {
      const response = await authenticatedApiCall(
        instance, accounts, 'GET', `/maestro-reports/${projectId}/batches`
      );
      if (response.data.success) {
        setBatches(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching batches:', err);
    } finally {
      setBatchesLoading(false);
    }
  }, [instance, accounts, projectId]);

  const fetchExchangeRate = useCallback(async () => {
    if (!accounts || accounts.length === 0) return;
    try {
      const response = await authenticatedApiCall(
        instance, accounts, 'GET', '/maestro-reports/exchange-rate'
      );
      if (response.data.success) {
        setExchangeRates(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching exchange rate:', err);
    }
  }, [instance, accounts]);

  const fetchData = useCallback(async (batchId) => {
    if (!projectId || !accounts || accounts.length === 0) return;
    setDataLoading(true);
    setDataError(null);
    try {
      const url = batchId
        ? `/maestro-reports/${projectId}/data?batch_id=${batchId}`
        : `/maestro-reports/${projectId}/data`;
      const response = await authenticatedApiCall(instance, accounts, 'GET', url);
      if (response.data.success) {
        const master = response.data.master_currency || null;
        setMasterCurrency(master);
        setMasterSource({
          source: response.data.division_source || null,
          sourceLabel: response.data.division_source_label || null,
          detail: response.data.division_detail || null,
          divisionName: response.data.division_name || null,
          divisionNumber: response.data.division_number || null,
          conflicts: response.data.division_conflicts || [],
        });
        if (!currencyViewExplicitRef.current) {
          setCurrencyView(master || 'native');
        }
        setData(response.data.data || []);
        if (response.data.batch_id) setActiveBatchId(response.data.batch_id);
        if (response.data.division_currencies) setDivisionCurrencies(response.data.division_currencies);
      } else {
        setDataError(response.data.error || 'Failed to load data');
      }
    } catch (err) {
      console.error('Error fetching maestro data:', err);
      setDataError(err.message || 'Failed to load data');
    } finally {
      setDataLoading(false);
    }
  }, [instance, accounts, projectId]);

  useEffect(() => {
    fetchBatches();
    fetchData();
    fetchExchangeRate();
  }, [fetchBatches, fetchData, fetchExchangeRate]);

  const handleBatchSelect = (batchId) => {
    if (batchId === activeBatchId) return;
    setActiveBatchId(batchId);
    fetchData(batchId);
  };

  const handleDeleteBatch = async () => {
    if (!projectId || !activeBatchId || !accounts || accounts.length === 0 || deleteBusy) return;
    setDeleteBusy(true);
    setError(null);
    try {
      const response = await authenticatedApiCall(
        instance, accounts, 'DELETE', `/maestro-reports/${projectId}/batches/${activeBatchId}`
      );
      if (!response.data.success) {
        setError(response.data.error || 'Failed to delete this import');
        return;
      }
      setDeleteOpen(false);
      const list = await authenticatedApiCall(
        instance, accounts, 'GET', `/maestro-reports/${projectId}/batches`
      );
      const next = list.data.success ? (list.data.data || []) : [];
      setBatches(next);
      if (next.length > 0) {
        setActiveBatchId(next[0].id);
        fetchData(next[0].id);
      } else {
        setActiveBatchId(null);
        setData([]);
      }
    } catch (err) {
      setError(err.message || 'Failed to delete this import');
    } finally {
      setDeleteBusy(false);
    }
  };

  const handleUploadSuccess = (result) => {
    setImportResult(result);
    setError(null);
    // Refresh batches list then load the new batch
    fetchBatches().then(() => {
      if (result.batch_id) {
        fetchData(result.batch_id);
      } else {
        fetchData();
      }
    });
  };

  const handleUploadError = (message) => {
    setError(message);
    setImportResult(null);
  };

  const [seedLoading, setSeedLoading] = useState(false);
  const handleSeedTestData = useCallback(async () => {
    if (!projectId || !accounts || accounts.length === 0) return;
    setSeedLoading(true);
    setError(null);
    try {
      const response = await authenticatedApiCall(
        instance, accounts, 'POST', `/maestro-reports/${projectId}/seed-test`
      );
      if (response.data.success) {
        setImportResult({
          inserted: response.data.data.inserted,
          total: response.data.data.inserted,
          batch_id: response.data.data.batch_id,
        });
        fetchBatches().then(() => fetchData(response.data.data.batch_id));
      } else {
        setError(response.data.error || 'Failed to seed test data');
      }
    } catch (err) {
      setError(err.message || 'Failed to seed test data');
    } finally {
      setSeedLoading(false);
    }
  }, [instance, accounts, projectId, fetchBatches, fetchData]);

  const handleDownloadConsolidated = useCallback(async () => {
    if (!projectId || !accounts || accounts.length === 0) return;
    setConsolidatedLoading(true);
    try {
      const url = activeBatchId
        ? `/maestro-reports/${projectId}/report-data?batch_id=${activeBatchId}`
        : `/maestro-reports/${projectId}/report-data`;
      const response = await authenticatedApiCall(instance, accounts, 'GET', url);
      if (response.data.success && response.data.data) {
        if (consolidatedCurrencyBlocked(response.data.data)) {
          setError("This file has rows in more than one currency and the portal cannot tell which company owns the job. Set the project's Division or link its estimate number (Admin → Projects), then try again.");
          return;
        }
        const branding = { instance, accounts };
        if (consolidatedMode === 'rollup') {
          await generateRollupReport(response.data.data, branding);
        } else {
          await generateConsolidatedSummaryReport(response.data.data, branding);
        }
      } else {
        setError(response.data.error || 'Failed to load report data');
      }
    } catch (err) {
      console.error('Error generating consolidated report:', err);
      setError(err.message || 'Failed to generate consolidated report');
    } finally {
      setConsolidatedLoading(false);
    }
  }, [instance, accounts, projectId, activeBatchId, consolidatedMode]);

  const handleDownload9091 = useCallback(async () => {
    if (!projectId || !accounts || accounts.length === 0) return;
    setReportLoading(true);
    try {
      const url = activeBatchId
        ? `/maestro-reports/${projectId}/report-data?batch_id=${activeBatchId}`
        : `/maestro-reports/${projectId}/report-data`;
      const response = await authenticatedApiCall(instance, accounts, 'GET', url);
      if (response.data.success && response.data.data) {
        await generate9091Report(response.data.data, { instance, accounts });
      } else {
        setError(response.data.error || 'Failed to load report data');
      }
    } catch (err) {
      console.error('Error generating 9091 report:', err);
      setError(err.message || 'Failed to generate report');
    } finally {
      setReportLoading(false);
    }
  }, [instance, accounts, projectId, activeBatchId]);

  const activeBatch = batches.find((b) => String(b.id) === String(activeBatchId)) || null;
  const activeBatchCaption = maestroBatchCaption(activeBatch);

  return (
    <div style={{ padding: '24px' }}>

      {/* ── Compact Toolbar ── */}
      <div style={{
        backgroundColor: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '14px 18px',
        marginBottom: '16px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          {/* Import */}
          <button
            onClick={() => setUploadModalOpen(true)}
            style={{
              padding: '0.5rem 1rem', backgroundColor: '#f3f4f6', color: '#374151',
              border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '0.875rem',
              fontWeight: '500', cursor: 'pointer', display: 'flex', alignItems: 'center',
              gap: '0.5rem', transition: 'all 0.2s ease', whiteSpace: 'nowrap',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#2563eb'; e.currentTarget.style.color = 'white'; e.currentTarget.style.borderColor = '#2563eb'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#f3f4f6'; e.currentTarget.style.color = '#374151'; e.currentTarget.style.borderColor = '#e5e7eb'; }}
          >
            📤 Import
          </button>

          {/* Seed test data — dev only */}
          {process.env.NODE_ENV !== 'production' && (
            <button
              onClick={handleSeedTestData}
              disabled={seedLoading}
              title="Insert dummy Maestro data for testing"
              style={{
                padding: '0.5rem 1rem', backgroundColor: '#f3f4f6', color: '#374151',
                border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '0.875rem',
                fontWeight: '500', cursor: seedLoading ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                transition: 'all 0.2s ease', whiteSpace: 'nowrap',
                opacity: seedLoading ? 0.6 : 1,
              }}
              onMouseEnter={(e) => { if (!seedLoading) { e.currentTarget.style.backgroundColor = '#f59e0b'; e.currentTarget.style.color = 'white'; e.currentTarget.style.borderColor = '#f59e0b'; } }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#f3f4f6'; e.currentTarget.style.color = '#374151'; e.currentTarget.style.borderColor = '#e5e7eb'; }}
            >
              🧪 {seedLoading ? 'Seeding...' : 'Seed Test Data'}
            </button>
          )}

          {/* Download 9091's */}
          {data.length > 0 && (
            <button
              onClick={handleDownload9091}
              disabled={reportLoading}
              style={{
                padding: '0.5rem 1rem', backgroundColor: '#f3f4f6', color: '#374151',
                border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '0.875rem',
                fontWeight: '500', cursor: reportLoading ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                transition: 'all 0.2s ease', whiteSpace: 'nowrap',
                opacity: reportLoading ? 0.6 : 1,
              }}
              onMouseEnter={(e) => { if (!reportLoading) { e.currentTarget.style.backgroundColor = '#059669'; e.currentTarget.style.color = 'white'; e.currentTarget.style.borderColor = '#059669'; } }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#f3f4f6'; e.currentTarget.style.color = '#374151'; e.currentTarget.style.borderColor = '#e5e7eb'; }}
            >
              📥 {reportLoading ? 'Generating...' : "9091's"}
            </button>
          )}

          {/* Consolidated: inline radio + download */}
          {data.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', color: '#374151', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                <input type="radio" name="consolidatedMode" value="byDiscipline"
                  checked={consolidatedMode === 'byDiscipline'}
                  onChange={() => setConsolidatedMode('byDiscipline')}
                  style={{ accentColor: '#7c3aed', cursor: 'pointer', margin: 0 }}
                />
                By Discipline
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', color: '#374151', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                <input type="radio" name="consolidatedMode" value="rollup"
                  checked={consolidatedMode === 'rollup'}
                  onChange={() => setConsolidatedMode('rollup')}
                  style={{ accentColor: '#7c3aed', cursor: 'pointer', margin: 0 }}
                />
                Roll-Up
              </label>
              <button
                onClick={handleDownloadConsolidated}
                disabled={consolidatedLoading}
                style={{
                  padding: '0.5rem 1rem', backgroundColor: '#f3f4f6', color: '#374151',
                  border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '0.875rem',
                  fontWeight: '500', cursor: consolidatedLoading ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  transition: 'all 0.2s ease', whiteSpace: 'nowrap',
                  opacity: consolidatedLoading ? 0.6 : 1,
                }}
                onMouseEnter={(e) => { if (!consolidatedLoading) { e.currentTarget.style.backgroundColor = '#7c3aed'; e.currentTarget.style.color = 'white'; e.currentTarget.style.borderColor = '#7c3aed'; } }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#f3f4f6'; e.currentTarget.style.color = '#374151'; e.currentTarget.style.borderColor = '#e5e7eb'; }}
              >
                📄 {consolidatedLoading ? 'Generating...' : 'Consolidated'}
              </button>
            </div>
          )}

          {/* Snapshot picker + delete, same height as the other toolbar buttons */}
          {batches.length > 0 && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <select
                value={activeBatchId || ''}
                onChange={(e) => handleBatchSelect(parseInt(e.target.value))}
                disabled={dataLoading || batchesLoading}
                title={activeBatchCaption || 'Import snapshot'}
                style={{
                  padding: '0.5rem 2rem 0.5rem 1rem', fontSize: '0.875rem', fontWeight: '500',
                  border: '1px solid #e5e7eb', borderRadius: '6px',
                  backgroundColor: '#f3f4f6', color: '#374151',
                  cursor: dataLoading ? 'not-allowed' : 'pointer',
                  appearance: 'none', WebkitAppearance: 'none',
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2.5' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center',
                  minWidth: '260px', maxWidth: '420px',
                }}
              >
                {batches.map((batch, idx) => (
                  <option key={batch.id} value={batch.id}>
                    {formatMaestroBatchOption(batch, { isLatest: idx === 0 })}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setDeleteOpen(true)}
                disabled={!activeBatchId || deleteBusy || dataLoading}
                aria-label="Delete this import"
                title="Delete this import. Audit records the delete."
                style={{
                  padding: '0.5rem 0.65rem', backgroundColor: '#f3f4f6', color: '#6b7280',
                  border: '1px solid #e5e7eb', borderRadius: '6px',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  cursor: (!activeBatchId || deleteBusy || dataLoading) ? 'not-allowed' : 'pointer',
                  opacity: (!activeBatchId || deleteBusy) ? 0.6 : 1,
                }}
                onMouseEnter={(e) => {
                  if (deleteBusy || dataLoading) return;
                  e.currentTarget.style.backgroundColor = '#fef2f2';
                  e.currentTarget.style.color = '#b91c1c';
                  e.currentTarget.style.borderColor = '#fecaca';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#f3f4f6';
                  e.currentTarget.style.color = '#6b7280';
                  e.currentTarget.style.borderColor = '#e5e7eb';
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M3 6h18" />
                  <path d="M8 6V4h8v2" />
                  <path d="M19 6l-1 14H6L5 6" />
                  <path d="M10 11v6" />
                  <path d="M14 11v6" />
                </svg>
              </button>
            </div>
          )}

          {/* Export to Excel — filtered (as displayed) or the complete table */}
          {data.length > 0 && (
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setShowExportMenu(prev => !prev)}
                disabled={exporting}
                title="Export to Excel — choose filtered (as displayed) or the complete table"
                style={{
                  padding: '0.5rem 1rem', backgroundColor: '#f3f4f6', color: '#374151',
                  border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '0.875rem',
                  fontWeight: '500', cursor: exporting ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  transition: 'all 0.2s ease', whiteSpace: 'nowrap',
                  opacity: exporting ? 0.6 : 1,
                }}
                onMouseEnter={(e) => { if (!exporting) { e.currentTarget.style.backgroundColor = '#0d9488'; e.currentTarget.style.color = 'white'; e.currentTarget.style.borderColor = '#0d9488'; } }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#f3f4f6'; e.currentTarget.style.color = '#374151'; e.currentTarget.style.borderColor = '#e5e7eb'; }}
              >
                📊 {exporting ? 'Exporting...' : 'Export ▾'}
              </button>
              {showExportMenu && (
                <>
                  <div onClick={() => setShowExportMenu(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1400 }} />
                  <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: '4px', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', overflow: 'hidden', zIndex: 1500, minWidth: '250px' }}>
                    <div
                      style={{ padding: '10px 16px', cursor: 'pointer', fontSize: '13px', color: '#374151' }}
                      onClick={() => handleExportExcel('filtered')}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      Filtered — as displayed ({displayData.length} rows)
                    </div>
                    <div
                      style={{ padding: '10px 16px', cursor: 'pointer', fontSize: '13px', color: '#374151' }}
                      onClick={() => handleExportExcel('all')}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      Complete table ({convertedData.length} rows)
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Audit log — imports, deletes, dev seeds, exchange-rate changes */}
          <button
            onClick={() => setAuditLogOpen(true)}
            title="Change history: Maestro imports, deletes, seeds and exchange-rate updates"
            style={{
              padding: '0.5rem 1rem', backgroundColor: '#f3f4f6', color: '#374151',
              border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '0.875rem',
              fontWeight: '500', cursor: 'pointer', display: 'flex', alignItems: 'center',
              gap: '0.5rem', transition: 'all 0.2s ease', whiteSpace: 'nowrap',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#1e3a5f'; e.currentTarget.style.color = 'white'; e.currentTarget.style.borderColor = '#1e3a5f'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#f3f4f6'; e.currentTarget.style.color = '#374151'; e.currentTarget.style.borderColor = '#e5e7eb'; }}
          >
            📜 Audit
          </button>

          {/* Columns personalization (visibility + order, saved per user) */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowColumnConfig(prev => !prev)}
              title="Choose which columns to show and in what order — saved for you on this browser"
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: showColumnConfig ? '#eef2ff' : '#f3f4f6',
                color: showColumnConfig ? '#4338ca' : '#374151',
                border: `1px solid ${showColumnConfig ? '#c7d2fe' : '#e5e7eb'}`, borderRadius: '6px',
                fontSize: '0.875rem', fontWeight: '500', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                transition: 'all 0.2s ease', whiteSpace: 'nowrap',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#6366f1'; e.currentTarget.style.color = 'white'; e.currentTarget.style.borderColor = '#6366f1'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = showColumnConfig ? '#eef2ff' : '#f3f4f6'; e.currentTarget.style.color = showColumnConfig ? '#4338ca' : '#374151'; e.currentTarget.style.borderColor = showColumnConfig ? '#c7d2fe' : '#e5e7eb'; }}
            >
              🗂 Columns
            </button>
            {showColumnConfig && (
              <>
                {/* Click-away layer */}
                <div
                  onClick={() => setShowColumnConfig(false)}
                  style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1400 }}
                />
                <div style={{
                  position: 'absolute', top: '100%', left: 0, marginTop: '4px',
                  backgroundColor: 'white', borderRadius: '8px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)', padding: '12px',
                  zIndex: 1500, width: '340px', maxHeight: '520px', overflowY: 'auto',
                }}>
                  <ColumnConfigPanel
                    title="Maestro Columns"
                    columns={colOrder.orderedColumns}
                    isVisible={(key) => visibleColumns.has(key)}
                    onToggle={(key) => {
                      setVisibleColumns(prev => {
                        const next = new Set(prev);
                        if (next.has(key)) { if (next.size > 1) next.delete(key); }
                        else next.add(key);
                        return next;
                      });
                    }}
                    onMove={colOrder.moveColumn}
                    onShowAll={() => setVisibleColumns(new Set(TABLE_COLUMNS.map(c => c.key)))}
                    onHideAll={() => setVisibleColumns(new Set(MIN_COLUMN_KEYS))}
                    onReset={() => {
                      // Default button: restore original order, all columns visible, auto widths
                      colOrder.resetOrder();
                      setVisibleColumns(new Set(TABLE_COLUMNS.map(c => c.key)));
                      setManualWidths({});
                    }}
                  />
                </div>
              </>
            )}
          </div>

          {/* Auto-fit column widths to content (clears manual drag adjustments) */}
          <button
            onClick={handleAutoFit}
            title="Auto-fit all column widths to their content (clears manual adjustments)"
            style={{
              padding: '0.5rem 1rem', backgroundColor: '#f3f4f6', color: '#374151',
              border: '1px solid #e5e7eb', borderRadius: '6px',
              fontSize: '0.875rem', fontWeight: '500', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              transition: 'all 0.2s ease', whiteSpace: 'nowrap',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#15803d'; e.currentTarget.style.color = 'white'; e.currentTarget.style.borderColor = '#15803d'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#f3f4f6'; e.currentTarget.style.color = '#374151'; e.currentTarget.style.borderColor = '#e5e7eb'; }}
          >
            ↔ Auto-fit
          </button>

          {/* Help button */}
          <button
            onClick={() => setHelpOpen(true)}
            style={{
              padding: '0.5rem 1rem', backgroundColor: '#f3f4f6', color: '#374151',
              border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '0.875rem',
              fontWeight: '500', cursor: 'pointer', display: 'flex', alignItems: 'center',
              gap: '0.5rem', transition: 'all 0.2s ease', whiteSpace: 'nowrap',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#dc2626'; e.currentTarget.style.color = 'white'; e.currentTarget.style.borderColor = '#dc2626'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#f3f4f6'; e.currentTarget.style.color = '#374151'; e.currentTarget.style.borderColor = '#e5e7eb'; }}
          >
            ❓ Help
          </button>

          <span style={{ fontSize: '13px', color: '#6b7280', whiteSpace: 'nowrap' }}>
            Imported Data
            {data.length > 0 && (
              <span style={{ fontWeight: '500', color: '#374151' }}> ({data.length} rows)</span>
            )}
          </span>
          <button
            type="button"
            onClick={() => fetchData(activeBatchId)}
            disabled={dataLoading}
            style={{
              padding: '0.5rem 1rem', backgroundColor: '#f3f4f6', color: '#374151',
              border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '0.875rem',
              fontWeight: '500', cursor: dataLoading ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              whiteSpace: 'nowrap', opacity: dataLoading ? 0.6 : 1,
            }}
          >
            {dataLoading ? 'Refreshing...' : 'Refresh'}
          </button>
          {data.length > 0 && sortConfig.key && (
            <span style={{ fontSize: '12px', color: '#6b7280', whiteSpace: 'nowrap' }}>
              Sorted by <strong>{TABLE_COLUMNS.find(c => c.key === sortConfig.key)?.label || sortConfig.key}</strong> {sortConfig.direction === 'asc' ? '↑' : '↓'}
              {' · '}
              <button
                type="button"
                onClick={() => setSortConfig({ key: null, direction: 'asc' })}
                style={{ color: '#2563eb', border: 'none', background: 'none', cursor: 'pointer', fontSize: '12px', textDecoration: 'underline', padding: 0 }}
              >
                Clear sort
              </button>
            </span>
          )}

          {/* Right-side: currency view toggle + exchange rate with visible timestamp */}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '10px' }}>
            {/* View $ in: Native / CAD / USD */}
            {data.length > 0 && (
              <div
                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}
                title={
                  'Show every dollar column in one currency. Rows from companies in the other currency are converted at the current exchange rate. Quantities, hours and percentages are never converted.' +
                  (masterCurrency && masterSource?.sourceLabel
                    ? `\n\nProject currency ${masterCurrency}: ${masterSource.divisionName || ''} ${masterSource.divisionNumber || ''} — ${masterSource.sourceLabel}${masterSource.detail ? ` (${masterSource.detail})` : ''}.`
                    : '')
                }
              >
                <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>View $:</span>
                <div style={{ display: 'inline-flex', border: '1px solid #e5e7eb', borderRadius: '6px', overflow: 'hidden' }}>
                  {[
                    { id: 'native', label: 'Native' },
                    ...(masterCurrency && masterCurrency !== 'CAD' && masterCurrency !== 'USD'
                      ? [{ id: masterCurrency, label: `${masterCurrency} (project)` }]
                      : []),
                    { id: 'CAD', label: masterCurrency === 'CAD' ? 'CAD (project)' : 'CAD' },
                    { id: 'USD', label: masterCurrency === 'USD' ? 'USD (project)' : 'USD' },
                  ].map((opt, i) => (
                    <button
                      key={opt.id}
                      onClick={() => chooseCurrencyView(opt.id)}
                      style={{
                        padding: '6px 10px', fontSize: '12px', fontWeight: '600',
                        border: 'none',
                        borderLeft: i > 0 ? '1px solid #e5e7eb' : 'none',
                        cursor: 'pointer',
                        backgroundColor: currencyView === opt.id ? '#1d4ed8' : '#f9fafb',
                        color: currencyView === opt.id ? '#fff' : '#6b7280',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Active conversion chip (when a single-currency view is on) */}
            {data.length > 0 && activeConversion && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                padding: '6px 12px', backgroundColor: '#fefce8', border: '1px solid #fde68a',
                borderRadius: '6px', fontSize: '12px', color: '#92400e', whiteSpace: 'nowrap',
              }}>
                <span style={{ fontWeight: '600' }}>All $ in {activeConversion.to}</span>
                <span>·</span>
                <span>
                  {activeConversion.from} × <span style={{ fontFamily: 'monospace', fontWeight: '700' }}>{activeConversion.rate.toFixed(6)}</span>
                </span>
              </span>
            )}

            {data.length > 0 && (() => {
              const cadUsd = exchangeRates.find(r => r.from_currency === 'CAD' && r.to_currency === 'USD');
              if (!cadUsd) return null;
              const lastUpdated = new Date(cadUsd.updated_at).toLocaleDateString('en-US', {
                year: 'numeric', month: 'short', day: 'numeric',
              });
              return (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  padding: '6px 12px', backgroundColor: '#f0f9ff', border: '1px solid #bae6fd',
                  borderRadius: '6px', fontSize: '12px', color: '#0369a1', whiteSpace: 'nowrap',
                }}>
                  <span style={{ fontWeight: '600' }}>CAD→USD</span>
                  <span style={{ fontFamily: 'monospace', fontWeight: '700', color: '#1d4ed8' }}>
                    {parseFloat(cadUsd.rate).toFixed(6)}
                  </span>
                  <span style={{ color: '#94a3b8', fontSize: '11px' }}>({lastUpdated})</span>
                </span>
              );
            })()}
          </div>
        </div>

        {data.length > 0 && !masterCurrency && (
          <div style={{
            marginTop: '10px',
            padding: '8px 12px',
            backgroundColor: '#fffbeb',
            border: '1px solid #fde68a',
            borderRadius: '6px',
            fontSize: '12px',
            color: '#92400e',
            lineHeight: 1.45,
          }}>
            The portal could not tell which company owns this job (no Division set, and the estimate number
            and job-number prefix do not identify one), so dollar columns may mix CAD and USD.{' '}
            <a href="/admin" style={{ color: '#b45309', fontWeight: 600 }}>
              Set the Division or link the estimate number in Admin → Projects
            </a>
          </div>
        )}

        {data.length > 0 && masterCurrency && masterSource?.conflicts?.length > 0 && (
          <div style={{
            marginTop: '10px',
            padding: '8px 12px',
            backgroundColor: '#fffbeb',
            border: '1px solid #fde68a',
            borderRadius: '6px',
            fontSize: '12px',
            color: '#92400e',
            lineHeight: 1.45,
          }}>
            <strong>Company check:</strong> dollars are shown in {masterCurrency} because the project's company is{' '}
            {masterSource.divisionName} {masterSource.divisionNumber} ({masterSource.sourceLabel}
            {masterSource.detail ? `, ${masterSource.detail}` : ''}). But{' '}
            {masterSource.conflicts.map((c, i) => (
              <span key={`${c.source}-${c.divisionNumber}`}>
                {i > 0 ? '; ' : ''}the {c.sourceLabel} says {c.divisionName} {c.divisionNumber}
                {c.detail ? ` (${c.detail})` : ''}
              </span>
            ))}
            . Ask Accounting which company this job belongs to and fix it in Admin → Projects.
          </div>
        )}

        {activeBatchCaption && (
          <div style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280', lineHeight: 1.4 }}>
            {activeBatchCaption}
          </div>
        )}

        {helpOpen && <MaestroDashboardHelp onClose={() => setHelpOpen(false)} />}

        {/* Success message */}
        {importResult && (
          <div style={{
            marginTop: '10px',
            padding: '10px 14px',
            backgroundColor: importResult.errors?.length > 0 ? '#fffbeb' : '#d1fae5',
            border: `1px solid ${importResult.errors?.length > 0 ? '#fcd34d' : '#6ee7b7'}`,
            borderRadius: '6px',
            color: importResult.errors?.length > 0 ? '#92400e' : '#065f46',
            fontSize: '13px',
          }}>
            <strong>Import complete.</strong>
            {' '}Inserted {importResult.inserted} of {importResult.total} rows.
            {importResult.errors && importResult.errors.length > 0 && (
              <div style={{ marginTop: '8px' }}>
                <div style={{ fontWeight: '600', marginBottom: '4px', color: '#b45309', fontSize: '12px' }}>
                  {importResult.errors.length} row(s) skipped:
                </div>
                <div style={{
                  maxHeight: '120px', overflowY: 'auto',
                  border: '1px solid #fcd34d', borderRadius: '4px',
                  backgroundColor: '#fff', fontSize: '11px', fontFamily: 'monospace',
                }}>
                  {importResult.errors.map((e, idx) => (
                    <div key={idx} style={{
                      padding: '4px 8px',
                      borderBottom: idx < importResult.errors.length - 1 ? '1px solid #fef3c7' : 'none',
                      color: '#374151',
                    }}>
                      <span style={{ fontWeight: '700', color: '#b45309', marginRight: '6px' }}>Row {e.excelRow ?? e.row}</span>
                      {e.identity && <span style={{ color: '#6b7280', marginRight: '6px' }}>{e.identity}</span>}
                      <span style={{ color: '#ef4444' }}>{e.error}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Error message */}
        {error && (
          <div style={{
            marginTop: '10px', padding: '10px 14px',
            backgroundColor: '#fee2e2', border: '1px solid #fca5a5',
            borderRadius: '6px', color: '#991b1b', fontSize: '13px',
          }}>
            <strong>Error:</strong> {error}
          </div>
        )}
      </div>

      {/* Data Table Section */}
      <div style={{
        backgroundColor: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '16px',
      }}>
        {/* Loading overlay for batch switch */}
        {dataLoading && data.length > 0 && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '12px 16px',
            backgroundColor: '#eff6ff',
            border: '1px solid #bfdbfe',
            borderRadius: '6px',
            marginBottom: '16px',
            fontSize: '14px',
            color: '#1e40af',
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
              style={{ animation: 'spin 0.8s linear infinite', flexShrink: 0 }}>
              <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
              <path d="M21 12a9 9 0 1 1-6.219-8.56" />
            </svg>
            Loading import data...
          </div>
        )}

        {dataLoading && data.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
            Loading data...
          </div>
        )}

        {dataError && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#fee2e2',
            border: '1px solid #fca5a5',
            borderRadius: '6px',
            color: '#991b1b',
            marginBottom: '16px'
          }}>
            {dataError}
          </div>
        )}

        {!dataLoading && !dataError && data.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#9ca3af',
            fontSize: '14px'
          }}>
            No data imported yet. Use the Import button above to upload a Maestro Excel file.
          </div>
        )}

        {/* Active filters summary */}
        {activeFilters.length > 0 && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
            padding: '8px 12px', marginBottom: '12px',
            backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px',
            fontSize: '12px', color: '#1e40af',
          }}>
            <span style={{ fontWeight: '600' }}>Active filters:</span>
            {activeFilters.map((f, i) => {
              const colLabel = TABLE_COLUMNS.find(c => c.key === f.key)?.label || f.key;
              return (
                <span key={i} style={{
                  padding: '2px 8px', backgroundColor: '#dbeafe', borderRadius: '10px',
                  fontSize: '11px', fontWeight: '500',
                }}>
                  {colLabel}: {f.type === 'values' ? `${f.count} values` : `${f.mode} "${f.value}"`}
                </span>
              );
            })}
            <button
              onClick={() => filterTemplate.clearAllFilters()}
              style={{
                padding: '2px 10px', fontSize: '11px', border: 'none', borderRadius: '10px',
                backgroundColor: '#ef4444', color: '#fff', cursor: 'pointer', fontWeight: '500',
              }}
            >
              Clear All
            </button>
            <span style={{ marginLeft: 'auto', color: '#6b7280', fontSize: '11px' }}>
              Showing {displayData.length} of {data.length} rows
            </span>
          </div>
        )}

        {displayData.length > 0 && (
          <VirtualTable
            key={activeBatchId}
            data={displayData}
            columns={visibleCols}
            colWidths={effectiveWidths}
            onResize={handleColumnResize}
            loading={dataLoading}
            sortConfig={sortConfig}
            onSort={handleSort}
            filterTemplate={filterTemplate}
          />
        )}

        {data.length > 0 && displayData.length === 0 && !dataLoading && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280', fontSize: '14px' }}>
            No rows match the current filters.{' '}
            <button
              onClick={() => filterTemplate.clearAllFilters()}
              style={{ color: '#2563eb', border: 'none', background: 'none', cursor: 'pointer', textDecoration: 'underline', fontSize: '14px' }}
            >
              Clear all filters
            </button>
          </div>
        )}
      </div>

      {/* Filtering Template dropdown (reusable module) */}
      {filterTemplate.openFilterColumn && (
        <FilterTemplateDropdown
          columnKey={filterTemplate.openFilterColumn}
          columnLabel={filterTemplate.columnLabels[filterTemplate.openFilterColumn]}
          showTextFilters={true}
          isNumericColumn={filterTemplate.numericColumnKeys.includes(filterTemplate.openFilterColumn)}
          columnFilters={filterTemplate.columnFilters}
          filterMode={filterTemplate.filterMode}
          filterText={filterTemplate.filterText}
          filterSearch={filterTemplate.filterSearch}
          filterButtonPositions={filterTemplate.filterButtonPositions}
          setOpenFilterColumn={filterTemplate.setOpenFilterColumn}
          setFilterSearch={filterTemplate.setFilterSearch}
          getUniqueValues={filterTemplate.getUniqueValues}
          handleFilterToggle={filterTemplate.handleFilterToggle}
          selectAllColumnFilter={filterTemplate.selectAllColumnFilter}
          clearColumnFilter={filterTemplate.clearColumnFilter}
          applyTextFilter={filterTemplate.applyTextFilter}
        />
      )}

      {/* Upload Modal */}
      <UploadMaestroModal
        isOpen={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onUploadSuccess={handleUploadSuccess}
        onUploadError={handleUploadError}
        projectId={projectId}
      />

      <AuditLogModal
        isOpen={auditLogOpen}
        onClose={() => setAuditLogOpen(false)}
        endpoint={`/maestro-reports/${projectId}/audit-log`}
        title="Maestro Audit Log"
        accentColor="#1e3a5f"
      />

      {deleteOpen && activeBatch && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2100,
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '12px', width: '92%', maxWidth: '440px',
            padding: '1.25rem 1.5rem', boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
          }}>
            <h3 style={{ margin: '0 0 8px', fontSize: '1.05rem', color: '#111827' }}>Delete this import?</h3>
            <p style={{ margin: '0 0 8px', fontSize: '13px', color: '#374151', lineHeight: 1.5 }}>
              {formatMaestroBatchOption(activeBatch)}
            </p>
            <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#6b7280', lineHeight: 1.5 }}>
              The rows in this snapshot are removed. Forecast numbers already copied onto the Forecast page stay there. Audit records who deleted it, the cut-off, the note, and the row count.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button
                type="button"
                onClick={() => { if (!deleteBusy) setDeleteOpen(false); }}
                disabled={deleteBusy}
                style={{
                  padding: '8px 14px', border: '1px solid #e5e7eb', borderRadius: '6px',
                  backgroundColor: '#fff', color: '#374151', cursor: 'pointer', fontSize: '13px',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteBatch}
                disabled={deleteBusy}
                style={{
                  padding: '8px 14px', border: 'none', borderRadius: '6px',
                  backgroundColor: '#b91c1c', color: 'white', cursor: deleteBusy ? 'not-allowed' : 'pointer',
                  fontSize: '13px', fontWeight: 600, opacity: deleteBusy ? 0.7 : 1,
                }}
              >
                {deleteBusy ? 'Deleting…' : 'Delete import'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaestroReports;
